// src/components/ProductList.tsx
import React, { useDeferredValue, useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import i18n from '../i18n/i18n';
import { ProductApi, CategoryApi, CompanyApi, PersonalCabinetApi, UserPrintSettingsApi, AdminUserApi } from '../api';
import { Category, Product, getProductBarcode, getProductQRCode } from '../types/Product';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import ProductForm from './ProductForm';
import Barcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';
import ReactDOMServer from 'react-dom/server';
import { getImageUrl } from '../utils/imageUtils';
import { exportPrintSettings, parsePrintSettings } from '../utils/printSettingsFile';
import { INVENTORY_CHANGED, InventoryChange } from '../utils/inventoryEvents';
// <<< NEW: use jsbarcode to generate a print-safe dataURL
// @ts-ignore
import JsBarcode from 'jsbarcode';

// Typed wrapper for i18n.t used outside React components (avoids TS overload issues)
const tGlobal = (key: string): string => (i18n as any).t(key);

// ---------- PAGE/GRID CONSTANTS (A4 + fixed margins) ----------
const PAPER_WIDTH = 210;    // A4 mm
const PAPER_HEIGHT = 297;   // A4 mm
type Company = { id: number; name: string; location?: string };

// ---------- SMALL UTILS ----------
const mm = (n: number) => `${n}mm`;

// <<< NEW: build a dataURL barcode for printing (no React effects needed)
function barcodeDataURL(value: string) {
  const canvas = document.createElement('canvas');
  // @ts-ignore
  JsBarcode(canvas, value, {
    format: 'CODE128',
    lineColor: '#000000',
    background: '#ffffff',
    height: 40,
    width: 2,
    margin: 0,
    displayValue: true,
    fontSize: 12,
    textMargin: 2,
  });
  return canvas.toDataURL('image/png'); // inline, print-safe
}

/**
 * Print-safe barcode: SVG (live) for screen/table, dataURL <img> for print window
 */
function renderBarcode(
  value: string,
  forPrintOrOpts?: boolean | { forPrint?: boolean; inTable?: boolean }
) {
  const forPrint = typeof forPrintOrOpts === 'boolean' ? forPrintOrOpts : Boolean(forPrintOrOpts?.forPrint);
  const inTable = typeof forPrintOrOpts === 'object' ? Boolean(forPrintOrOpts?.inTable) : false;

  if (forPrint) {
    const src = barcodeDataURL(value);
    return (
      <div style={{
        width: '100%',
        height: '18mm',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
      }}>
        <img
          src={src}
          alt={value}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            imageRendering: 'auto',
          }}
        />
      </div>
    );
  }

  // Screen / table view -> React component (effects will run)
  // If in table context, don't force container width/height so it keeps original size.
  const containerStyle: React.CSSProperties = inTable
    ? { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff' }
    : { width: '100%', height: '18mm', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff' };

  // Use a slightly thicker default in non-table contexts; keep library defaults-ish for table
  const barWidth = inTable ? 2 : 1.8;
  const barHeight = 40;
  const fontSize = 12;

  return (
    <div style={containerStyle}>
      <Barcode
        value={value}
        lineColor="#000000"
        background="#ffffff"
        height={barHeight}
        width={barWidth}
        margin={0}
        displayValue={true}
        format="CODE128"
        renderer="svg"
        textAlign="center"
        textPosition="bottom"
        fontSize={fontSize}
        textMargin={2}
      />
    </div>
  );
}

function labelKey(p: Product, i: number) {
  return `${p.id ?? p.name}-${i}`;
}

// ---------- LABEL CONTENT ----------
const LabelContent = React.memo(({
  product,
  showBarcode,
  showQR,
  forPrint = false,
  rotate = false,
  currency = 'Lei',
  labelSections = []
}: {
  product: Product;
  showBarcode: boolean;
  showQR: boolean;
  forPrint?: boolean;
  rotate?: boolean;
  currency?: string;
  labelSections?: Array<{
    id: string;
    name: string;
    content: string[];
    fontSize: number;
    fontFamily: string;
    fontWeight: string;
    fontStyle: string;
    textAlign: string;
    color: string;
    backgroundColor: string;
    padding: number;
    gap: number;
    enabled: boolean;
    showFieldLabels?: boolean;
  }>;
}) => {
  const isPrint = Boolean(forPrint);
  // Use Product ID for barcode and QR code
  const barcodeValue = getProductBarcode(product);
  const qrCodeValue = getProductQRCode(product);

  // Estimate sizes for header text to fit within label without overflow
  function computeHeaderSizes(text: string) {
    const baseHeaderMm = rotate ? 2.5 : 2.8; // slightly smaller for rotated
    const baseLineMm = rotate ? 2.0 : 2.2;   // slightly smaller for rotated
    if (!text) return { headerMm: baseHeaderMm, lineMm: baseLineMm, clamp: 3 };
    // Remove markdown markers for length estimation
    const cleaned = text.replace(/\*\*\*|\*\*/g, '').replace(/\*/g, '');
    const lines = cleaned.split('\n');
    const maxLen = Math.max(0, ...lines.map(l => l.trim().length));
    const lineCount = Math.max(1, lines.length);
    // Heuristic: target ~18 chars/line and max 3 lines
    const targetPerLine = rotate ? 14 : 18; // less chars per line when rotated
    let scale = 1;
    if (maxLen > targetPerLine) scale *= targetPerLine / maxLen;
    if (lineCount > 3) scale *= 3 / lineCount;
    scale = Math.min(1, Math.max(0.65, scale));
    const headerMm = +(baseHeaderMm * scale).toFixed(2);
    const lineMm = +(Math.max(1.6, baseLineMm * scale)).toFixed(2);
    return { headerMm, lineMm, clamp: 3 as const };
  }
  const headerFit = computeHeaderSizes(product.headerText || '');

  // Function to render formatted header text
  const renderHeaderText = (text: string) => {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, index) => {
      const processLine = (txt: string) => {
        const parts: React.ReactNode[] = [];
        let remainingText = txt;
        let key = 0;
        while (remainingText.length > 0) {
          const boldItalicMatch = remainingText.match(/^\*\*\*(.*?)\*\*\*/);
          if (boldItalicMatch) {
            parts.push(<span key={key++} style={{ fontWeight: 'bold', fontStyle: 'italic' }}>{boldItalicMatch[1]}</span>);
            remainingText = remainingText.slice(boldItalicMatch[0].length);
            continue;
          }
          const boldMatch = remainingText.match(/^\*\*(.*?)\*\*/);
          if (boldMatch) {
            parts.push(<span key={key++} style={{ fontWeight: 'bold' }}>{boldMatch[1]}</span>);
            remainingText = remainingText.slice(boldMatch[0].length);
            continue;
          }
          const italicMatch = remainingText.match(/^\*(.*?)\*/);
          if (italicMatch) {
            parts.push(<span key={key++} style={{ fontStyle: 'italic' }}>{italicMatch[1]}</span>);
            remainingText = remainingText.slice(italicMatch[0].length);
            continue;
          }
          const nextFormatIndex = remainingText.search(/\*+/);
          const nextChunk = nextFormatIndex === -1 ? remainingText : remainingText.slice(0, nextFormatIndex);
          if (nextChunk) {
            parts.push(<span key={key++}>{nextChunk}</span>);
            remainingText = remainingText.slice(nextChunk.length);
          } else {
            parts.push(<span key={key++}>{remainingText[0]}</span>);
            remainingText = remainingText.slice(1);
          }
        }
        return parts;
      };
      return (
        <div key={index} style={{ fontSize: `${headerFit.lineMm}mm`, lineHeight: '1.12', marginBottom: index < lines.length - 1 ? '0.3mm' : '0' }}>
          {processLine(line)}
        </div>
      );
    });
  };

  // Function to render content based on field name with optional label
  const renderField = (field: string, showLabel: boolean = false, style: React.CSSProperties = {}, textAlign: string = 'center') => {
    const fieldLabels: Record<string, string> = {
      category: 'Category',
      model: 'Model',
      firm: 'Firm',
      size: 'Size',
      price: 'Price',
      barcode: 'Barcode',
      qr: 'QR',
      headerText: 'Header'
    };

    // Get text value directly
    const getTextValue = () => {
      switch (field) {
        case 'category':
          return product.category?.name;
        case 'model':
          return product.model;
        case 'firm':
          return product.firm;
        case 'size':
          return product.size;
        case 'price':
          return `${product.price}${currency}`;
        default:
          return null;
      }
    };

    // Handle special fields
    if (field === 'barcode') {
      return (
        <div className="label-barcode" style={{ 
          width: 'auto', 
          height: rotate ? '12mm' : '16mm',
          background: '#fff',
          padding: rotate ? '1mm' : '1.5mm',
          borderRadius: rotate ? '0.5mm' : '1mm'
        }}>
          {renderBarcode(barcodeValue, isPrint)}
        </div>
      );
    }

    if (field === 'qr') {
      return (
        <div className="label-qrcode" style={{ 
          width: 'auto', 
          height: 'auto',
          background: '#fff',
          padding: rotate ? '1mm' : '1.5mm',
          borderRadius: rotate ? '0.5mm' : '1mm'
        }}>
          <QRCodeSVG value={qrCodeValue} size={rotate ? 45 : 60} level="M" />
        </div>
      );
    }

    if (field === 'headerText') {
      return product.headerText ? renderHeaderText(product.headerText) : null;
    }

    const textValue = getTextValue();
    if (!textValue) return null;

    if (showLabel) {
      return (
        <span style={{...style}}>
          <span style={{ color: '#999', textTransform: 'lowercase' }}>
            {fieldLabels[field].toLowerCase()}:
          </span>{' '}
          {textValue}
        </span>
      );
    }

    return <span style={{...style}}>{textValue}</span>;
  };

  // Render section based on template
  const renderSection = (section: typeof labelSections[0], isRotated: boolean) => {
    if (!section.enabled || section.content.length === 0) return null;

    // Check if section has both text fields and code fields (barcode/qr)
    const textFields = ['category', 'model', 'firm', 'size', 'price', 'headerText'];
    const codeFields = ['barcode', 'qr'];
    const hasTextFields = section.content.some(f => textFields.includes(f));
    const hasCodeFields = section.content.some(f => codeFields.includes(f));
    const hasMixedContent = hasTextFields && hasCodeFields;

    const sectionStyle: React.CSSProperties = {
      fontSize: `${section.fontSize}mm`,
      fontFamily: section.fontFamily,
      fontWeight: section.fontWeight as any,
      fontStyle: section.fontStyle as any,
      textAlign: section.textAlign as any,
      color: section.color,
      backgroundColor: section.backgroundColor,
      padding: `${section.padding}mm`,
      lineHeight: '1.2',
      borderRadius: section.backgroundColor !== 'transparent' ? '1mm' : '0'
    };

    // Category section - simple display
    if (section.id === 'category') {
      if (hasMixedContent) {
        return (
          <div key={section.id} style={{
            ...sectionStyle,
            textTransform: 'uppercase',
            letterSpacing: '0.3mm',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '1mm'
          }}>
            <div style={{flex: 1, textAlign: section.textAlign as any}}>
              {section.content.filter(f => !codeFields.includes(f)).map((field, idx) => (
                <React.Fragment key={`${field}-${idx}`}>
                  {renderField(field, section.showFieldLabels, {}, section.textAlign)}
                  {idx < section.content.filter(f => !codeFields.includes(f)).length - 1 && ' '}
                </React.Fragment>
              ))}
            </div>
            {section.content.filter(f => codeFields.includes(f)).map((field, idx) => (
              <div key={`${field}-${idx}`} style={{flexShrink: 0}}>
                {renderField(field, section.showFieldLabels, {}, section.textAlign)}
              </div>
            ))}
          </div>
        );
      }
      return (
        <div key={section.id} style={{
          ...sectionStyle,
          textTransform: 'uppercase',
          letterSpacing: '0.3mm'
        }}>
          {section.content.map((field, idx) => (
            <React.Fragment key={`${field}-${idx}`}>
              {renderField(field, section.showFieldLabels, {}, section.textAlign)}
              {idx < section.content.length - 1 && ' '}
            </React.Fragment>
          ))}
        </div>
      );
    }

    // Top section - horizontal row
    if (section.id === 'top') {
      if (hasMixedContent) {
        return (
          <div key={section.id} style={{
            ...sectionStyle,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '1mm'
          }}>
            <div style={{flex: 1, textAlign: section.textAlign as any}}>
              {section.content.filter(f => !codeFields.includes(f)).map((field, idx) => (
                <React.Fragment key={`${field}-${idx}`}>
                  {renderField(field, section.showFieldLabels, {}, section.textAlign)}
                  {idx < section.content.filter(f => !codeFields.includes(f)).length - 1 && <span style={{margin: '0 2mm'}}> </span>}
                </React.Fragment>
              ))}
            </div>
            {section.content.filter(f => codeFields.includes(f)).map((field, idx) => (
              <div key={`${field}-${idx}`} style={{flexShrink: 0}}>
                {renderField(field, section.showFieldLabels, {}, section.textAlign)}
              </div>
            ))}
          </div>
        );
      }
      return (
        <div key={section.id} style={sectionStyle}>
          {section.content.map((field, idx) => (
            <React.Fragment key={`${field}-${idx}`}>
              {renderField(field, section.showFieldLabels, {}, section.textAlign)}
              {idx < section.content.length - 1 && <span style={{margin: '0 2mm'}}> </span>}
            </React.Fragment>
          ))}
        </div>
      );
    }

    // Middle section
    if (section.id === 'middle') {
      if (hasMixedContent) {
        return (
          <div key={section.id} style={{
            ...sectionStyle,
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '1mm'
          }}>
            <div style={{flex: 1, textAlign: section.textAlign as any}}>
              {section.content.filter(f => !codeFields.includes(f)).map((field, idx) => (
                <React.Fragment key={`${field}-${idx}`}>
                  {renderField(field, section.showFieldLabels, field === 'price' ? { 
                    fontSize: `${section.fontSize}mm`, 
                    fontWeight: section.fontWeight as any 
                  } : {}, section.textAlign)}
                  {idx < section.content.filter(f => !codeFields.includes(f)).length - 1 && <span style={{margin: '0 2mm'}}> </span>}
                </React.Fragment>
              ))}
            </div>
            {section.content.filter(f => codeFields.includes(f)).map((field, idx) => (
              <div key={`${field}-${idx}`} style={{flexShrink: 0}}>
                {renderField(field, section.showFieldLabels, {}, section.textAlign)}
              </div>
            ))}
          </div>
        );
      }
      return (
        <div key={section.id} style={{
          ...sectionStyle,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <div style={{textAlign: section.textAlign as any}}>
            {section.content.map((field, idx) => (
              <React.Fragment key={`${field}-${idx}`}>
                {renderField(field, section.showFieldLabels, field === 'price' ? { 
                  fontSize: `${section.fontSize}mm`, 
                  fontWeight: section.fontWeight as any 
                } : {}, section.textAlign)}
                {idx < section.content.length - 1 && <span style={{margin: '0 2mm'}}> </span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      );
    }

    // Bottom section - horizontal row
    if (section.id === 'bottom') {
      if (hasMixedContent) {
        return (
          <div key={section.id} style={{
            ...sectionStyle,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '1mm'
          }}>
            <div style={{flex: 1, textAlign: section.textAlign as any}}>
              {section.content.filter(f => !codeFields.includes(f)).map((field, idx) => (
                <React.Fragment key={`${field}-${idx}`}>
                  {renderField(field, section.showFieldLabels, {}, section.textAlign)}
                  {idx < section.content.filter(f => !codeFields.includes(f)).length - 1 && <span style={{margin: '0 2mm'}}> </span>}
                </React.Fragment>
              ))}
            </div>
            {section.content.filter(f => codeFields.includes(f)).map((field, idx) => (
              <div key={`${field}-${idx}`} style={{flexShrink: 0}}>
                {renderField(field, section.showFieldLabels, {}, section.textAlign)}
              </div>
            ))}
          </div>
        );
      }
      return (
        <div key={section.id} style={sectionStyle}>
          {section.content.map((field, idx) => (
            <React.Fragment key={`${field}-${idx}`}>
              {renderField(field, section.showFieldLabels, {}, section.textAlign)}
              {idx < section.content.length - 1 && <span style={{margin: '0 2mm'}}> </span>}
            </React.Fragment>
          ))}
        </div>
      );
    }

    // Fallback for any other section
    if (hasMixedContent) {
      return (
        <div key={section.id} style={{
          ...sectionStyle,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '1mm'
        }}>
          <div style={{flex: 1, textAlign: section.textAlign as any}}>
            {section.content.filter(f => !codeFields.includes(f)).map((field, idx) => (
              <React.Fragment key={`${field}-${idx}`}>
                {renderField(field, section.showFieldLabels, {}, section.textAlign)}
                {idx < section.content.filter(f => !codeFields.includes(f)).length - 1 && <span style={{margin: '0 2mm'}}> </span>}
              </React.Fragment>
            ))}
          </div>
          {section.content.filter(f => codeFields.includes(f)).map((field, idx) => (
            <div key={`${field}-${idx}`} style={{flexShrink: 0}}>
              {renderField(field, section.showFieldLabels, {}, section.textAlign)}
            </div>
          ))}
        </div>
      );
    }
    return (
      <div key={section.id} style={sectionStyle}>
        {section.content.map((field, idx) => (
          <React.Fragment key={`${field}-${idx}`}>
            {renderField(field, section.showFieldLabels, {}, section.textAlign)}
            {idx < section.content.length - 1 && <span style={{margin: '0 2mm'}}> </span>}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // If template sections are provided, use them
  if (labelSections && labelSections.length > 0) {
    try {
      return (
        <div className={`label-content ${rotate ? 'rotated-layout' : 'standard-layout'}`} style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          padding: rotate ? '1.5mm' : '2mm',
          justifyContent: 'space-between'
        }}>
          {labelSections.map(section => renderSection(section, rotate))}
        </div>
      );
    } catch (error) {
      console.error('Error rendering template sections:', error);
      // Fall through to default layout
    }
  }

  // FALLBACK: Original hardcoded layout if no template provided

  // ROTATED LAYOUT - Vertical structure: top (category, firm, model, size) → middle (price, barcode/QR) → bottom (custom header)
  if (rotate) {
    return (
      <div className="label-content rotated-layout" style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        padding: '1.5mm',
        justifyContent: 'space-between'
      }}>
        {/* Top Section: Category + Product Info (SMALL) */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '0.3mm',
          paddingBottom: '1mm'
        }}>
          <div style={{ fontSize: '1.6mm', color: '#666', lineHeight: '1.1', textTransform: 'uppercase' }}>
            {product.category?.name}
          </div>
          <div style={{ fontSize: '1.8mm', color: '#333', lineHeight: '1.2' }}>
            <b>{product.model}</b> | <b>{product.firm}</b> | Size: <b>{product.size}</b>
          </div>
        </div>

        {/* Middle Section: Price + Barcode/QR (BIG and BLACK) */}
        <div style={{ 
          flex: 1,
          display: 'flex', 
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2mm',
          background: '#000',
          padding: '2mm',
          borderRadius: '1mm'
        }}>
          <div className="label-price" style={{ 
            fontSize: '8mm', 
            fontWeight: '900', 
            color: '#fff',
            lineHeight: '1'
          }}>
            {product.price}{currency}
          </div>
          
          <div style={{ display: 'flex', gap: '2mm', alignItems: 'center' }}>
              <div className="label-barcode" style={{ 
                  width: 'auto', 
                  height: '12mm',
                  background: '#fff',
                  padding: '1mm',
                  borderRadius: '0.5mm'
                }}>
                  {renderBarcode(barcodeValue, isPrint)}
                </div>
              <div className="label-qrcode" style={{ 
                  width: 'auto', 
                  height: 'auto',
                  background: '#fff',
                  padding: '1mm',
                  borderRadius: '0.5mm'
                }}>
                  <QRCodeSVG value={qrCodeValue} size={45} level="M" />
                </div>
            </div>
        </div>

        {/* Bottom Section: Header Text from Product (SMALL) */}
        {product.headerText && (
          <div style={{
            fontSize: '2mm',
            color: '#333',
            padding: '0.8mm 1.5mm',
            textAlign: 'center',
            lineHeight: '1.2',
            marginTop: '1mm',
            fontWeight: '500'
          }}>
            {renderHeaderText(product.headerText)}
          </div>
        )}
      </div>
    );
  }

  // NON-ROTATED LAYOUT - Same vertical structure
  return (
    <div className="label-content standard-layout" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      width: '100%',
      height: '100%',
      padding: '2mm',
      justifyContent: 'space-between'
    }}>
      {/* Top Section: Category + Product Info (SMALL) */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '0.5mm',
        paddingBottom: '1.5mm'
      }}>
        <div style={{ fontSize: '2mm', color: '#666', lineHeight: '1.1', textTransform: 'uppercase', fontWeight: '500' }}>
          {product.category?.name}
        </div>
        <div style={{ fontSize: '2.2mm', color: '#333', lineHeight: '1.3' }}>
          <b>{product.model}</b> | <b>{product.firm}</b> | Size: <b>{product.size}</b>
        </div>
      </div>

      {/* Middle Section: Price + Barcode/QR (BIG and BLACK) */}
      <div style={{ 
        flex: 1,
        display: 'flex', 
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-around',
        gap: '3mm',
        background: '#000',
        padding: '3mm',
        borderRadius: '1.5mm'
      }}>
        <div className="label-price" style={{ 
          fontSize: '10mm', 
          fontWeight: '900', 
          color: '#fff',
          lineHeight: '1'
        }}>
          {product.price}{currency}
        </div>
        
        <div style={{ display: 'flex', gap: '3mm', alignItems: 'center' }}>
            <div className="label-barcode" style={{ 
              width: 'auto', 
              height: '16mm',
              background: '#fff',
              padding: '1.5mm',
              borderRadius: '1mm'
            }}>
              {renderBarcode(barcodeValue, isPrint)}
            </div>
            <div className="label-qrcode" style={{ 
              width: 'auto', 
              height: 'auto',
              background: '#fff',
              padding: '1.5mm',
              borderRadius: '1mm'
            }}>
              <QRCodeSVG value={qrCodeValue} size={60} level="M" />
            </div>
          </div>
      </div>

      {/* Bottom Section: Header Text from Product (SMALL) */}
      {product.headerText && (
        <div style={{
          fontSize: '2.2mm',
          color: '#333',
          padding: '1mm 2mm',
          textAlign: 'center',
          lineHeight: '1.3',
          marginTop: '1.5mm',
          fontWeight: '500'
        }}>
          {renderHeaderText(product.headerText)}
        </div>
      )}
    </div>
  );
});

// ---------- PRINT ENGINE ----------
function openPrintWindow(html: string) {
  const win = window.open('', '', 'width=900,height=1200');
  if (!win) return;
  win.document.write(html);
  win.document.close();
}

function buildPrintHtml({
  pagesMarkup,
  pageSize = 'A4',
  headerInfo,
  labelW,
  labelH,
  labelPadding = 2,
  pageMargins = { top: 10, right: 10, bottom: 10, left: 10 },
  printCellBorder = false,
  printFitContent = true
}: {
  pagesMarkup: string;
  pageSize?: 'A4' | 'auto';
  headerInfo?: {
    companyName?: string;
    companyLocation?: string;
    totalProducts: number;
    date: string;
  };
  labelW: number;
  labelH: number;
  labelPadding?: number;
  pageMargins?: { top: number; right: number; bottom: number; left: number };
  printCellBorder?: boolean;
  printFitContent?: boolean;
}){
  const headerMarkup = headerInfo ? `
    <div style="
      position: fixed;
      top: 5mm;
      left: 8mm;
      right: 8mm;
      height: 8mm;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 10px;
      color: #666;
      border-bottom: 1px solid #eee;
      padding-bottom: 2mm;
      margin-bottom: 2mm;
    ">
      <div>
        <strong>${tGlobal('print.systemName')}</strong>
        ${headerInfo.companyName ? `<br/>${tGlobal('print.company')}: ${headerInfo.companyName}` : ''}
        ${headerInfo.companyLocation ? ` (${headerInfo.companyLocation})` : ''}
      </div>
      <div style="text-align: right;">
        <div>${tGlobal('print.products')}: ${headerInfo.totalProducts}</div>
        <div>${headerInfo.date}</div>
      </div>
    </div>
  ` : '';

  // script to auto-fit content and trigger print
  const fitScript = printFitContent ? `
    <script>
      function fitLabels() {
        var labels = document.querySelectorAll('.label');
        labels.forEach(function(label) {
          var content = label.querySelector('.label-content');
          if (!content) return;

          // Reset any previous scale
          content.style.transform = 'none';
          content.style.transformOrigin = 'center center';

          var lw = label.offsetWidth;
          var lh = label.offsetHeight;

          // Temporarily unconstrain to measure natural size
          var prevW = content.style.width;
          var prevH = content.style.height;
          content.style.width = 'max-content';
          content.style.height = 'max-content';
          label.style.overflow = 'visible';

          var cw = content.scrollWidth || content.offsetWidth;
          var ch = content.scrollHeight || content.offsetHeight;

          // Restore
          content.style.width = prevW || '100%';
          content.style.height = prevH || '100%';
          label.style.overflow = 'hidden';

          if (cw === 0 || ch === 0) return;
          // Scale to fill the full cell (stretch up AND down)
          var scale = Math.min((lw - 2) / cw, (lh - 2) / ch);
          content.style.transform = 'scale(' + scale.toFixed(5) + ')';
        });
      }
      window.addEventListener('load', function() {
        fitLabels();
        setTimeout(function(){ window.print(); window.close(); }, 400);
      });
      window.addEventListener('beforeprint', fitLabels);
    </script>
  ` : `
    <script>
      window.addEventListener('load', function() {
        setTimeout(function(){ window.print(); window.close(); }, 300);
      });
    </script>
  `;

  return `
    <html>
      <head>
        <title>${tGlobal('print.printTitle')}</title>
        <style>
          @page { margin: 0; size: ${pageSize} portrait; }
          html, body { margin: 0; padding: 0; background: #fff; }
          .print-sheet { width: ${mm(PAPER_WIDTH)}; height: ${mm(PAPER_HEIGHT)}; position: relative; background: #fff; overflow: hidden; page-break-after: always; break-after: page; page-break-inside: avoid; break-inside: avoid; }
          .print-sheet:last-child { page-break-after: avoid; break-after: avoid; }
          .labels-page {
            position: absolute;
            top: ${mm(pageMargins.top + (headerInfo ? 12 : 0))};
            left: ${mm(pageMargins.left)};
            width: ${mm(PAPER_WIDTH - pageMargins.left - pageMargins.right)};
            height: ${mm(PAPER_HEIGHT - pageMargins.top - pageMargins.bottom - (headerInfo ? 12 : 0))};
            display: flex;
            flex-wrap: wrap;
            background: #fff;
            align-content: flex-start;
          }
          .label-cell {
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fff;
            box-sizing: border-box;
            overflow: hidden;
            position: relative;
            flex-shrink: 0;
            flex-grow: 0;
            page-break-inside: avoid;
          }
          .label {
            width: ${mm(labelW)};
            height: ${mm(labelH)};
            display: flex;
            justify-content: center;
            align-items: center;
            background: #fff;
            border: ${printCellBorder ? '1px solid #888' : '1px solid #eee'};
            overflow: hidden;
            box-sizing: border-box;
            padding: ${labelPadding}mm;
          }
          .label.rotated {
            transform: rotate(90deg);
            transform-origin: center center;
            width: ${mm(labelW)};
            height: ${mm(labelH)};
          }
          .label-content {
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: center;
            transform-origin: center center;
            overflow: visible;
            box-sizing: border-box;
          }
          .label-content.rotated-layout {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            padding: 1mm;
          }
          .label-title { font-weight: bold; font-size: 3.5mm; margin-bottom: 0.5mm; word-break: break-word; text-align: center; line-height: 1.1; }
          .label-row { font-size: 2.2mm; margin-bottom: .3mm; word-break: break-word; text-align: center; line-height: 1.1; }
          .label-price { font-size: 6mm; font-weight: 900; text-align: center; margin: 0.8mm 0; letter-spacing: .1mm; }

          /* barcode */
          .label-barcode {
            margin-top: .4mm;
            width: 95%;
            height: 16mm;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fff;
            overflow: visible;
          }
          .label-barcode img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            image-rendering: auto;
          }

          /* qr */
          .label-qrcode {
            margin-top: .4mm;
            width: 95%;
            height: 15mm;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: visible;
            position: relative;
            z-index: 10;
          }
          .label-qrcode svg {
            width: auto !important;
            height: 100% !important;
            max-height: 15mm !important;
            object-fit: contain;
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
          }
          @media print {
            .label-qrcode svg {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              color-adjust: exact;
            }
          }
          .rotated-layout .label-qrcode {
            width: auto;
            height: 100%;
            flex: 0 0 auto;
          }
        </style>
      </head>
      <body>
        ${headerMarkup}
        ${pagesMarkup}
        ${fitScript}
      </body>
    </html>
  `;
}

function renderPagesToStaticMarkup(pages: React.ReactNode[]) {
  const validPages = pages.filter(page => page !== null && page !== undefined);
  return ReactDOMServer.renderToStaticMarkup(
    <>
      {validPages.map((page, i) => (
        <div className="print-sheet" key={`sheet-${i}`}>
          <div className="labels-page">{page}</div>
        </div>
      ))}
    </>
  );
}

// ---------- PRINT LABEL COMPONENT (NO UI, JUST PRINT) ----------
function PrintLabel({
  products,
  count,
  numberOfCopies = 1,
  currency = 'Lei',
  labelW,
  labelH,
  gap,
  rotate,
  showBarcode,
  showQR,
  offset = 0,
  companies,
  selectedCompanyId,
  labelPadding = 2,
  pageMargins = { top: 10, right: 10, bottom: 10, left: 10 },
  labelSections = [],
  printCellBorder = false,
  printFitContent = true,
  onDone
}: {
  products: Product[];
  count: number;
  numberOfCopies?: number;
  currency?: string;
  labelW: number;
  labelH: number;
  gap: number;
  rotate: boolean;
  showBarcode: boolean;
  showQR: boolean;
  offset?: number;
  companies: Company[];
  selectedCompanyId: number | null;
  labelPadding?: number;
  pageMargins?: { top: number; right: number; bottom: number; left: number };
  labelSections?: Array<{
    id: string;
    name: string;
    content: string[];
    fontSize: number;
    fontFamily: string;
    fontWeight: string;
    fontStyle: string;
    textAlign: string;
    color: string;
    backgroundColor: string;
    padding: number;
    gap: number;
    enabled: boolean;
  }>;
  printCellBorder?: boolean;
  printFitContent?: boolean;
  onDone: () => void;
}){
  const usableWidth = PAPER_WIDTH - pageMargins.left - pageMargins.right;
  const usableHeight = PAPER_HEIGHT - pageMargins.top - pageMargins.bottom;

  const gridLabelW = rotate ? labelH : labelW;
  const gridLabelH = rotate ? labelW : labelH;

  const effectiveLabelW = gridLabelW + gap;
  const effectiveLabelH = gridLabelH + gap;

  const perRow = Math.max(1, Math.floor((usableWidth + gap) / effectiveLabelW));
  const perCol = Math.max(1, Math.floor((usableHeight + gap) / effectiveLabelH));
  const labelsPerPage = Math.max(1, perRow * perCol);

  const pages = useMemo(() => {
    // Apply numberOfCopies multiplier to total labels
    const totalLabelsInProducts = products.length * Math.max(1, count) * Math.max(1, numberOfCopies);
    const totalCells = offset + totalLabelsInProducts;
    if (totalCells === 0) return [];

    const totalPages = Math.ceil(totalCells / labelsPerPage);
    const allPages: React.ReactNode[] = [];

    for (let pageNum = 0; pageNum < totalPages; pageNum++) {
      const pageStartIndex = pageNum * labelsPerPage;
      const pageEndIndex = Math.min((pageNum + 1) * labelsPerPage, totalCells);
      const cellsInThisPage: React.ReactNode[] = [];

      for (let cellIndex = pageStartIndex; cellIndex < pageEndIndex; cellIndex++) {
        if (cellIndex < offset) {
          cellsInThisPage.push(
            <div
              className="label-cell"
              key={`offset-${cellIndex}`}
              style={{
                width: mm(gridLabelW),
                height: mm(gridLabelH),
                margin: mm(gap),
                minWidth: mm(gridLabelW),
                minHeight: mm(gridLabelH),
                maxWidth: mm(gridLabelW),
                maxHeight: mm(gridLabelH),
                flexShrink: 0,
                flexGrow: 0
              }}
            />
          );
        } else {
          const productLabelIndex = cellIndex - offset;
          let currentProduct: Product | null = null;
          let productIndex = 0;
          let copyIndex = 0;

          let labelCounter = 0;
          for (let i = 0; i < products.length; i++) {
            const copiesForThisProduct = Math.max(1, count);
            if (labelCounter + copiesForThisProduct > productLabelIndex) {
              currentProduct = products[i];
              productIndex = i;
              copyIndex = productLabelIndex - labelCounter;
              break;
            }
            labelCounter += copiesForThisProduct;
          }

          if (currentProduct) {
            cellsInThisPage.push(
              <div
                className="label-cell"
                key={labelKey(currentProduct, productIndex * 1000 + copyIndex)}
                style={{
                  width: mm(gridLabelW),
                  height: mm(gridLabelH),
                  margin: mm(gap),
                  minWidth: mm(gridLabelW),
                  minHeight: mm(gridLabelH),
                  maxWidth: mm(gridLabelW),
                  maxHeight: mm(gridLabelH),
                  flexShrink: 0,
                  flexGrow: 0
                }}
              >
                <div className={`label${rotate ? ' rotated' : ''}`}>
                  <LabelContent 
                    product={currentProduct} 
                    showBarcode={showBarcode} 
                    showQR={showQR} 
                    forPrint={true} 
                    rotate={rotate} 
                    currency={currency}
                    labelSections={labelSections}
                  />
                </div>
              </div>
            );
          }
        }
      }

      if (cellsInThisPage.length > 0) {
        allPages.push(<>{cellsInThisPage}</>);
      }
    }
    return allPages.filter(Boolean);
  }, [products, count, numberOfCopies, gap, rotate, showBarcode, showQR, offset, labelsPerPage, currency, gridLabelH, gridLabelW, labelSections]);

  useEffect(() => {
    const html = buildPrintHtml({
      pagesMarkup: renderPagesToStaticMarkup(pages),
      labelW,
      labelH,
      labelPadding,
      pageMargins,
      printCellBorder,
      printFitContent,
      headerInfo: undefined
    });
    openPrintWindow(html);
    onDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div style={{ display: 'none' }} />;
}

// ---------- SETTINGS POPUP ----------
// Page size presets (all dimensions in mm)
const PAGE_PRESETS = {
  // Standard paper sizes
  A3_PORTRAIT: { width: 297, height: 420, name: 'A3 Portrait', type: 'paper' },
  A3_LANDSCAPE: { width: 420, height: 297, name: 'A3 Landscape', type: 'paper' },
  A4_PORTRAIT: { width: 210, height: 297, name: 'A4 Portrait', type: 'paper' },
  A4_LANDSCAPE: { width: 297, height: 210, name: 'A4 Landscape', type: 'paper' },
  A5_PORTRAIT: { width: 148, height: 210, name: 'A5 Portrait', type: 'paper' },
  A5_LANDSCAPE: { width: 210, height: 148, name: 'A5 Landscape', type: 'paper' },
  LETTER_PORTRAIT: { width: 215.9, height: 279.4, name: 'Letter Portrait', type: 'paper' },
  LETTER_LANDSCAPE: { width: 279.4, height: 215.9, name: 'Letter Landscape', type: 'paper' },
  LEGAL_PORTRAIT: { width: 215.9, height: 355.6, name: 'Legal Portrait', type: 'paper' },
  
  // Check/Payment sizes (US standard check)
  CHECK_PERSONAL: { width: 152.4, height: 76.2, name: 'Personal Check', type: 'check' },
  CHECK_BUSINESS: { width: 203.2, height: 88.9, name: 'Business Check', type: 'check' },
  
  // Ticket/Receipt sizes
  TICKET_SMALL: { width: 50, height: 100, name: 'Small Ticket (50×100mm)', type: 'ticket' },
  TICKET_STANDARD: { width: 80, height: 150, name: 'Standard Ticket', type: 'ticket' },
  RECEIPT_ROLL: { width: 80, height: 210, name: 'Receipt Roll (80mm)', type: 'ticket' },
  THERMAL_58MM: { width: 58, height: 150, name: 'Thermal 58mm', type: 'ticket' },
  THERMAL_80MM: { width: 80, height: 200, name: 'Thermal 80mm', type: 'ticket' },
  
  // Single cell/card sizes
  BUSINESS_CARD: { width: 85, height: 55, name: 'Business Card', type: 'card' },
  CREDIT_CARD: { width: 85.6, height: 53.98, name: 'Credit Card Size', type: 'card' },
  INDEX_CARD_3X5: { width: 127, height: 76.2, name: 'Index Card 3×5"', type: 'card' },
  INDEX_CARD_4X6: { width: 152.4, height: 101.6, name: 'Index Card 4×6"', type: 'card' },
  POSTCARD: { width: 148, height: 105, name: 'Postcard (A6)', type: 'card' },
  
  // Shipping labels
  SHIPPING_4X6: { width: 101.6, height: 152.4, name: 'Shipping Label 4×6"', type: 'shipping' },
  SHIPPING_4X4: { width: 101.6, height: 101.6, name: 'Square Label 4×4"', type: 'shipping' },
  
  // Custom
  CUSTOM: { width: 210, height: 297, name: 'Custom Size', type: 'custom' },
};

// Label presets for multi-label sheets
const LABEL_PRESETS = {
  SMALL: { width: 38, height: 25, name: 'Small (38×25mm)' },
  MEDIUM: { width: 50, height: 30, name: 'Medium (50×30mm)' },
  LARGE: { width: 70, height: 40, name: 'Large (70×40mm)' },
  AVERY_5160: { width: 66.68, height: 25.4, name: 'Avery 5160' },
  AVERY_5161: { width: 101.6, height: 25.4, name: 'Avery 5161' },
  AVERY_5163: { width: 101.6, height: 50.8, name: 'Avery 5163 (2×4")' },
  DYMO_30252: { width: 62, height: 29, name: 'DYMO 30252' },
  DYMO_30256: { width: 89, height: 59, name: 'DYMO 30256 (Large)' },
  CUSTOM: { width: 0, height: 0, name: 'Custom' },
};

// Label Template Section Type
type LabelSection = {
  id: string;
  name: string;
  content: string[];
  fontSize: number;
  fontFamily: string;
  fontWeight: 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
  fontStyle: 'normal' | 'italic';
  textAlign: 'left' | 'center' | 'right';
  color: string;
  backgroundColor: string;
  padding: number;
  gap: number; // Gap between items within the section
  enabled: boolean;
  showFieldLabels: boolean; // Show field names like "Model:", "Size:", etc.
};

const PrintSettingsPopup = React.memo(({
  onClose,
  onConfirm,
  onSave,
  defaults,
  isPrintAll = false,
  products,
  onProductsConfirm
}: {
  onClose: () => void;
  onConfirm: (s: {
    width: number; height: number; count: number; numberOfCopies?: number; currency?: string; gap: number;
    showBarcode: boolean; showQR: boolean; rotate: boolean; offset: number;
    pageMarginTop?: number; pageMarginRight?: number; pageMarginBottom?: number; pageMarginLeft?: number;
    containerPadding?: number; cellPadding?: number; labelPadding?: number;
    columns?: number; cellWidth?: number; cellHeight?: number; cellGap?: number;
    pageSize?: string;
    labelSections?: LabelSection[];
    printCellBorder?: boolean;
    printFitContent?: boolean;
  }) => void;
  onSave?: (s: {
    width: number; height: number; count: number; numberOfCopies?: number; currency?: string; gap: number;
    showBarcode: boolean; showQR: boolean; rotate: boolean; offset: number;
    pageMarginTop?: number; pageMarginRight?: number; pageMarginBottom?: number; pageMarginLeft?: number;
    containerPadding?: number; cellPadding?: number; labelPadding?: number;
    columns?: number; cellWidth?: number; cellHeight?: number; cellGap?: number;
    pageSize?: string;
    labelSections?: LabelSection[];
    printCellBorder?: boolean;
    printFitContent?: boolean;
  }) => Promise<void>;
  defaults: { 
    width: number; height: number; gap: number; count: number; rotate: boolean; 
    showBarcode: boolean; showQR: boolean; offset: number;
    currency?: string;
    pageMarginTop?: number; pageMarginRight?: number; pageMarginBottom?: number; pageMarginLeft?: number;
    containerPadding?: number; cellPadding?: number; labelPadding?: number;
    columns?: number; cellWidth?: number; cellHeight?: number; cellGap?: number;
    pageSize?: string;
    labelSections?: LabelSection[];
    printCellBorder?: boolean;
    printFitContent?: boolean;
  };
  isPrintAll?: boolean;
  products?: Product[];
  onProductsConfirm?: (products: Product[]) => void;
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'products' | 'settings'>(products && products.length > 0 ? 'products' : 'settings');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Per-product copy counts for the Products tab
  const [copiesMap, setCopiesMap] = useState<Record<number, number>>(() => {
    const map: Record<number, number> = {};
    (products || []).forEach(p => { if (p.id != null) map[p.id] = defaults.count || 1; });
    return map;
  });

  // Drag state refs (no re-render during drag)
  const dragSectionIdx = React.useRef<number | null>(null);
  const dragOverSectionIdx = React.useRef<number | null>(null);
  const dragFieldKey = React.useRef<{ sectionIdx: number; fieldIdx: number } | null>(null);
  const dragOverFieldIdx = React.useRef<number | null>(null);
  const [dragOverSection, setDragOverSection] = React.useState<number | null>(null);
  const [dragOverField, setDragOverField] = React.useState<{ sectionIdx: number; fieldIdx: number } | null>(null);
  
  // Label settings
  const [width, setWidth] = useState(defaults.width);
  const [height, setHeight] = useState(defaults.height);
  const [count, setCount] = useState(defaults.count);
  const [numberOfCopies, setNumberOfCopies] = useState(1);
  const [currency, setCurrency] = useState(defaults.currency ?? 'Lei');
  const [useMmInput, setUseMmInput] = useState(false);
  const [gap, setGap] = useState(defaults.gap);
  const [showBarcode, setShowBarcode] = useState(defaults.showBarcode);
  const [showQR, setShowQR] = useState(defaults.showQR);
  const [rotate, setRotate] = useState(defaults.rotate);
  const [offset, setOffset] = useState(defaults.offset);
  const [labelPadding, setLabelPadding] = useState(defaults.labelPadding ?? 2);
  
  // Page settings
  const [pageSize, setPageSize] = useState(defaults.pageSize ?? 'A4_PORTRAIT');
  const [customPageWidth, setCustomPageWidth] = useState(210);
  const [customPageHeight, setCustomPageHeight] = useState(297);
  const [pageMarginTop, setPageMarginTop] = useState(defaults.pageMarginTop ?? 10);
  const [pageMarginRight, setPageMarginRight] = useState(defaults.pageMarginRight ?? 10);
  const [pageMarginBottom, setPageMarginBottom] = useState(defaults.pageMarginBottom ?? 10);
  const [pageMarginLeft, setPageMarginLeft] = useState(defaults.pageMarginLeft ?? 10);
  
  // Table settings
  const [columns, setColumns] = useState(defaults.columns ?? 4);
  const [rows, setRows] = useState(1);
  const [cellWidth, setCellWidth] = useState(defaults.cellWidth ?? 150);
  const [cellHeight, setCellHeight] = useState(defaults.cellHeight ?? 40);
  const [cellGap, setCellGap] = useState(defaults.cellGap ?? 8);
  const [cellHorizontalGap, setCellHorizontalGap] = useState(defaults.cellGap ?? 8);
  const [cellVerticalGap, setCellVerticalGap] = useState(defaults.cellGap ?? 8);
  const [containerPadding, setContainerPadding] = useState(defaults.containerPadding ?? 16);
  const [cellPadding, setCellPadding] = useState(defaults.cellPadding ?? 8);
  const [printCellBorder, setPrintCellBorder] = useState(defaults.printCellBorder ?? true);
  const [printFitContent, setPrintFitContent] = useState(defaults.printFitContent ?? true);
  
  // Label Template Configuration - use from defaults
  // Fields are displayed in the order they appear in the content array
  const [labelSections, setLabelSections] = useState<LabelSection[]>(defaults.labelSections ?? [
    {
      id: 'category',
      name: 'Category Section',
      content: ['category'],
      fontSize: 2.5,
      fontFamily: 'Arial',
      fontWeight: '700' as const,
      fontStyle: 'normal' as const,
      textAlign: 'center' as const,
      color: '#333333',
      backgroundColor: 'transparent',
      padding: 0.5,
      gap: 2,
      enabled: true,
      showFieldLabels: false
    },
    {
      id: 'top',
      name: 'Top Section',
      content: ['model', 'firm', 'size'],
      fontSize: 2,
      fontFamily: 'Arial',
      fontWeight: '500' as const,
      fontStyle: 'normal' as const,
      textAlign: 'center' as const,
      color: '#555555',
      backgroundColor: 'transparent',
      padding: 0.8,
      gap: 4,
      enabled: true,
      showFieldLabels: true
    },
    {
      id: 'middle',
      name: 'Middle Section',
      content: ['price'],
      fontSize: 10,
      fontFamily: 'Arial',
      fontWeight: '900' as const,
      fontStyle: 'normal' as const,
      textAlign: 'center' as const,
      color: '#000000',
      backgroundColor: 'transparent',
      padding: 1,
      gap: 5,
      enabled: true,
      showFieldLabels: false
    },
    {
      id: 'bottom',
      name: 'Bottom Section',
      content: ['barcode', 'qr', 'headerText'],
      fontSize: 1.8,
      fontFamily: 'Arial',
      fontWeight: '400' as const,
      fontStyle: 'normal' as const,
      textAlign: 'center' as const,
      color: '#333333',
      backgroundColor: 'transparent',
      padding: 0.8,
      gap: 4,
      enabled: true,
      showFieldLabels: false
    }
  ]);
  
  // Single cell mode (one item per page)
  const [singleCellMode, setSingleCellMode] = useState(false);
  
  // Auto-calculation flag
  const [autoCalculate, setAutoCalculate] = useState(true);

  // Conversion helper: px to mm (assuming 96 DPI standard)
  const pxToMm = (px: number) => +(px * 0.264583).toFixed(1);
  const mmToPx = (mm: number) => Math.round(mm / 0.264583);

  // Get effective page dimensions
  const getPageDimensions = () => {
    if (pageSize === 'CUSTOM') {
      return { width: customPageWidth, height: customPageHeight };
    }
    const preset = PAGE_PRESETS[pageSize as keyof typeof PAGE_PRESETS];
    return preset ? { width: preset.width, height: preset.height } : { width: 210, height: 297 };
  };

  // Calculate available space
  const getAvailableSpace = () => {
    const page = getPageDimensions();
    return {
      width: page.width - pageMarginLeft - pageMarginRight - (containerPadding * 2),
      height: page.height - pageMarginTop - pageMarginBottom - (containerPadding * 2)
    };
  };

  // Auto-calculate columns based on cell size
  const calculateColumnsFromSize = (cellW: number, hGap: number) => {
    const available = getAvailableSpace();
    const cols = Math.floor((available.width + hGap) / (cellW + hGap));
    return Math.max(1, cols);
  };

  // Auto-calculate rows based on cell size
  const calculateRowsFromSize = (cellH: number, vGap: number) => {
    const available = getAvailableSpace();
    const r = Math.floor((available.height + vGap) / (cellH + vGap));
    return Math.max(1, r);
  };

  // Auto-calculate cell size based on columns and rows
  const calculateCellSizeFromLayout = (cols: number, r: number, hGap: number, vGap: number) => {
    const available = getAvailableSpace();
    const cellW = (available.width - (hGap * (cols - 1))) / cols;
    const cellH = (available.height - (vGap * (r - 1))) / r;
    return { width: Math.max(10, cellW), height: Math.max(10, cellH) };
  };

  const handleLabelPreset = (preset: keyof typeof LABEL_PRESETS) => {
    const p = LABEL_PRESETS[preset];
    if (preset !== 'CUSTOM') {
      setWidth(p.width);
      setHeight(p.height);
      
      // Auto-calculate columns if enabled
      if (autoCalculate) {
        const cols = calculateColumnsFromSize(p.width, cellHorizontalGap);
        setColumns(cols);
        const r = calculateRowsFromSize(p.height, cellVerticalGap);
        setRows(r);
      }
    }
  };

  const handlePagePreset = (preset: keyof typeof PAGE_PRESETS) => {
    setPageSize(preset);
    const p = PAGE_PRESETS[preset];
    
    // Update custom dimensions if custom selected
    if (preset === 'CUSTOM') {
      setCustomPageWidth(p.width);
      setCustomPageHeight(p.height);
    }
    
    // Auto-enable single cell mode for check, ticket, card, and shipping types
    if (['check', 'ticket', 'card', 'shipping'].includes(p.type)) {
      setSingleCellMode(true);
      // Set label size to match page size for single cell mode
      setWidth(p.width);
      setHeight(p.height);
      // Set minimal margins for these formats
      setPageMarginTop(5);
      setPageMarginRight(5);
      setPageMarginBottom(5);
      setPageMarginLeft(5);
      // Single column, no gap
      setColumns(1);
      setRows(1);
      setCellGap(0);
      setCellHorizontalGap(0);
      setCellVerticalGap(0);
    } else {
      // For standard paper, suggest multi-label layout
      if (singleCellMode) {
        setSingleCellMode(false);
        setColumns(4);
        setRows(1);
        setCellGap(8);
        setCellHorizontalGap(8);
        setCellVerticalGap(8);
      }
    }
  };

  const handleMarginPreset = (type: 'none' | 'narrow' | 'normal' | 'wide') => {
    const presets = {
      none: { top: 0, right: 0, bottom: 0, left: 0 },
      narrow: { top: 5, right: 5, bottom: 5, left: 5 },
      normal: { top: 10, right: 10, bottom: 10, left: 10 },
      wide: { top: 20, right: 20, bottom: 20, left: 20 },
    };
    const p = presets[type];
    setPageMarginTop(p.top);
    setPageMarginRight(p.right);
    setPageMarginBottom(p.bottom);
    setPageMarginLeft(p.left);
  };
  
  // Handle cell size change - update columns
  const handleCellWidthChange = (newWidth: number) => {
    setCellWidth(newWidth);
    if (autoCalculate) {
      const cols = calculateColumnsFromSize(newWidth, cellHorizontalGap);
      setColumns(cols);
    }
  };

  const handleCellHeightChange = (newHeight: number) => {
    setCellHeight(newHeight);
    if (autoCalculate) {
      const r = calculateRowsFromSize(newHeight, cellVerticalGap);
      setRows(r);
    }
  };

  // Handle columns change - update cell size
  const handleColumnsChange = (cols: number) => {
    setColumns(cols);
    if (autoCalculate) {
      const newSize = calculateCellSizeFromLayout(cols, rows, cellHorizontalGap, cellVerticalGap);
      setCellWidth(Math.round(newSize.width * 10) / 10);
    }
  };

  const handleRowsChange = (r: number) => {
    setRows(r);
    if (autoCalculate) {
      const newSize = calculateCellSizeFromLayout(columns, r, cellHorizontalGap, cellVerticalGap);
      setCellHeight(Math.round(newSize.height * 10) / 10);
    }
  };

  // Handle gap changes
  const handleHorizontalGapChange = (gap: number) => {
    setCellHorizontalGap(gap);
    setCellGap(gap); // Keep backward compatibility
    if (autoCalculate) {
      const newSize = calculateCellSizeFromLayout(columns, rows, gap, cellVerticalGap);
      setCellWidth(Math.round(newSize.width * 10) / 10);
    }
  };

  const handleVerticalGapChange = (gap: number) => {
    setCellVerticalGap(gap);
    if (autoCalculate) {
      const newSize = calculateCellSizeFromLayout(columns, rows, cellHorizontalGap, gap);
      setCellHeight(Math.round(newSize.height * 10) / 10);
    }
  };
  
  const toggleSingleCellMode = () => {
    const newMode = !singleCellMode;
    setSingleCellMode(newMode);
    
    if (newMode) {
      // Single cell: one item per page
      const preset = PAGE_PRESETS[pageSize as keyof typeof PAGE_PRESETS];
      if (preset) {
        setWidth(preset.width - pageMarginLeft - pageMarginRight);
        setHeight(preset.height - pageMarginTop - pageMarginBottom);
      }
      setColumns(1);
      setCount(1);
      setCellGap(0);
      setGap(0);
    } else {
      // Multi-label: restore defaults
      setColumns(4);
      setCount(1);
      setCellGap(8);
      setGap(2);
    }
  };

  const [transferStatus, setTransferStatus] = useState<'' | 'loaded' | 'failed'>('');
  const [importingSettings, setImportingSettings] = useState(false);
  const settingsSnapshot = () => ({ width, height, count, numberOfCopies, currency, useMmInput, gap, showBarcode, showQR, rotate, offset, labelPadding, pageSize, customPageWidth, customPageHeight, pageMarginTop, pageMarginRight, pageMarginBottom, pageMarginLeft, columns, rows, cellWidth, cellHeight, cellGap, cellHorizontalGap, cellVerticalGap, containerPadding, cellPadding, printCellBorder, printFitContent, labelSections });
  const importSettings = async (file: File) => {
    setImportingSettings(true); setTransferStatus('');
    try {
      if (file.size > 256 * 1024) throw new Error('File too large');
      const settings = parsePrintSettings(await file.text(), settingsSnapshot(), Object.keys(PAGE_PRESETS));
      setWidth(settings.width);
      setHeight(settings.height);
      setCount(settings.count);
      setNumberOfCopies(settings.numberOfCopies);
      setCurrency(settings.currency);
      setUseMmInput(settings.useMmInput);
      setGap(settings.gap);
      setShowBarcode(settings.showBarcode);
      setShowQR(settings.showQR);
      setRotate(settings.rotate);
      setOffset(settings.offset);
      setLabelPadding(settings.labelPadding);
      setPageSize(settings.pageSize);
      setCustomPageWidth(settings.customPageWidth);
      setCustomPageHeight(settings.customPageHeight);
      setPageMarginTop(settings.pageMarginTop);
      setPageMarginRight(settings.pageMarginRight);
      setPageMarginBottom(settings.pageMarginBottom);
      setPageMarginLeft(settings.pageMarginLeft);
      setColumns(settings.columns);
      setRows(settings.rows);
      setCellWidth(settings.cellWidth);
      setCellHeight(settings.cellHeight);
      setCellGap(settings.cellGap);
      setCellHorizontalGap(settings.cellHorizontalGap);
      setCellVerticalGap(settings.cellVerticalGap);
      setContainerPadding(settings.containerPadding);
      setCellPadding(settings.cellPadding);
      setPrintCellBorder(settings.printCellBorder);
      setPrintFitContent(settings.printFitContent);
      setLabelSections(settings.labelSections);
      setSaveStatus('idle'); setTransferStatus('loaded'); setActiveTab('settings');
    } catch { setTransferStatus('failed'); }
    finally { setImportingSettings(false); }
  };

  return (
    <div style={{ 
      position: 'fixed', 
      inset: 0, 
      background: 'rgba(0,0,0,0.6)', 
      backdropFilter: 'blur(4px)',
      zIndex: 1000, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '20px',
      overflow: 'auto'
    }}>
      <div style={{ 
        background: '#fff', 
        borderRadius: 16, 
        width: 'auto',
        minWidth: '680px',
        maxWidth: '900px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Minimalist Header */}
        <div style={{ 
          padding: '32px 32px 24px', 
          borderBottom: '1px solid #f0f0f0',
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: '#fff'
        }}>
          <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: '-0.5px', color: 'white' }}>{t('print.printSettings')}</h2>
          <p style={{ margin: '8px 0 0', opacity: 0.95, fontSize: 15, fontWeight: 400 }}>{t('print.configureLabelLayout')}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
            <button type="button" disabled={importingSettings || saveStatus === 'saving'} onClick={() => {
              const settings = settingsSnapshot();
              try {
                const contents = exportPrintSettings(settings);
                parsePrintSettings(contents, settings, Object.keys(PAGE_PRESETS));
                saveAs(new Blob([contents], { type: 'application/json' }), 'stockflow-print-settings.json');
                setTransferStatus('');
              } catch { setTransferStatus('failed'); }
            }}>{t('print.exportSettings')}</button>
            <label style={{ cursor: 'pointer', fontSize: 13 }}>{t('print.importSettings')}
              <input type="file" accept=".json,application/json" disabled={importingSettings || saveStatus === 'saving'} style={{ display: 'block', maxWidth: 260, marginTop: 4 }} onChange={event => {
                const file = event.target.files?.[0]; event.target.value = ''; if (file) void importSettings(file);
              }} />
            </label>
          </div>
          {transferStatus && <p role={transferStatus === 'failed' ? 'alert' : 'status'} style={{ margin: '10px 0 0', fontSize: 13 }}>{t(transferStatus === 'loaded' ? 'print.settingsImported' : 'print.settingsFileError')}</p>}
          {/* Tab navigation */}
          <div style={{ display: 'flex', gap: 4, marginTop: 20 }}>
            {products && products.length > 0 && (
              <button
                onClick={() => setActiveTab('products')}
                style={{
                  padding: '8px 20px',
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  background: activeTab === 'products' ? '#fff' : 'rgba(255,255,255,0.18)',
                  color: activeTab === 'products' ? '#1565c0' : '#fff',
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'all 0.15s'
                }}
              >
                📦 {t('print.productsTab', { count: products.length })}
              </button>
            )}
            <button
              onClick={() => setActiveTab('settings')}
              style={{
                padding: '8px 20px',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                background: activeTab === 'settings' ? '#fff' : 'rgba(255,255,255,0.18)',
                color: activeTab === 'settings' ? '#1565c0' : '#fff',
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              ⚙️ {t('print.configurationTab')}
            </button>
          </div>
        </div>

        {/* Tab: Products */}
        {activeTab === 'products' && products && products.length > 0 && (
          <div style={{ padding: '24px 32px', flex: 1, overflow: 'auto' }}>
            <p style={{ margin: '0 0 16px', fontSize: 14, color: '#555' }}>
              {t('print.copiesHelp')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {products.map((p, idx) => (
                <div
                  key={p.id ?? idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 14px',
                    background: idx % 2 === 0 ? '#f8f9fa' : '#fff',
                    borderRadius: 8,
                    border: '1px solid #e8e8e8'
                  }}
                >
                  {/* Product info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name || '—'}
                    </div>
                    <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>
                      {[p.firm, p.model, p.size].filter(Boolean).join(' · ')}
                      {p.price != null && <span style={{ marginLeft: 8, color: '#1976d2', fontWeight: 500 }}>{p.price} {defaults.currency ?? 'Lei'}</span>}
                    </div>
                  </div>
                  {/* Copies stepper */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <button
                      onClick={() => {
                        if (p.id == null) return;
                        setCopiesMap(prev => ({ ...prev, [p.id!]: Math.max(0, (prev[p.id!] ?? 1) - 1) }));
                      }}
                      style={{
                        width: 30, height: 30,
                        border: '1px solid #d0d0d0',
                        borderRadius: 6,
                        background: '#fff',
                        color: '#333',
                        fontWeight: 700,
                        fontSize: 18,
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        lineHeight: 1
                      }}
                    >−</button>
                    <span style={{ minWidth: 32, textAlign: 'center', fontWeight: 700, fontSize: 16, color: '#1976d2' }}>
                      {p.id != null ? (copiesMap[p.id] ?? 1) : 1}
                    </span>
                    <button
                      onClick={() => {
                        if (p.id == null) return;
                        setCopiesMap(prev => ({ ...prev, [p.id!]: (prev[p.id!] ?? 1) + 1 }));
                      }}
                      style={{
                        width: 30, height: 30,
                        border: '1px solid #1976d2',
                        borderRadius: 6,
                        background: '#1976d2',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 18,
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        lineHeight: 1
                      }}
                    >+</button>
                  </div>
                </div>
              ))}
            </div>
            {/* Quick set all */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, padding: '12px 14px', background: '#e3f2fd', borderRadius: 8 }}>
              <span style={{ fontSize: 13, color: '#1565c0', fontWeight: 500 }}>{t('print.setAllTo')}</span>
              {[1, 2, 3, 5].map(n => (
                <button
                  key={n}
                  onClick={() => {
                    const map: Record<number, number> = {};
                    (products || []).forEach(p => { if (p.id != null) map[p.id] = n; });
                    setCopiesMap(map);
                  }}
                  style={{
                    padding: '4px 12px',
                    border: '1px solid #1976d2',
                    borderRadius: 6,
                    background: '#fff',
                    color: '#1976d2',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer'
                  }}
                >×{n}</button>
              ))}
              <span style={{ fontSize: 12, color: '#888', marginLeft: 'auto' }}>
                {t('print.totalLabels', { count: (products || []).reduce<number>((sum, p) => sum + (p.id != null ? (copiesMap[p.id] ?? 1) : 1), 0) })}
              </span>
            </div>
          </div>
        )}

        {/* Tab: Settings (Scrollable Content) */}
        {activeTab === 'settings' && (
        <div style={{ padding: '32px', flex: 1, overflow: 'auto' }}>
          <div style={{ display: 'grid', gap: 24 }}>

            {/* ══ LABEL CELL APPEARANCE (shown at top when Print All) ══ */}
            {isPrintAll && (
              <section style={{ padding: 20, background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)', borderRadius: 12, border: '2px solid #1976d2' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 17, color: '#0d47a1', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                  🏷️ {t('print.labelCellAppearance')}
                </h3>

                {/* Toggles row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                  {/* Cell border */}
                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                    padding: '12px 14px', borderRadius: 8,
                    background: printCellBorder ? '#e8f5e9' : '#fff',
                    border: printCellBorder ? '2px solid #4caf50' : '2px solid #ddd',
                    transition: 'all 0.15s'
                  }}>
                    <input
                      type="checkbox"
                      checked={printCellBorder}
                      onChange={e => setPrintCellBorder(e.target.checked)}
                      style={{ width: 18, height: 18, marginTop: 1, cursor: 'pointer', accentColor: '#4caf50' }}
                    />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#2e7d32' }}>🔲 {t('print.showLabelBorder')}</div>
                      <div style={{ fontSize: 11, color: '#666', marginTop: 3 }}>{t('print.labelBorderHelp')}</div>
                    </div>
                  </label>

                  {/* Fit content */}
                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                    padding: '12px 14px', borderRadius: 8,
                    background: printFitContent ? '#e3f2fd' : '#fff',
                    border: printFitContent ? '2px solid #1976d2' : '2px solid #ddd',
                    transition: 'all 0.15s'
                  }}>
                    <input
                      type="checkbox"
                      checked={printFitContent}
                      onChange={e => setPrintFitContent(e.target.checked)}
                      style={{ width: 18, height: 18, marginTop: 1, cursor: 'pointer', accentColor: '#1976d2' }}
                    />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1565c0' }}>📐 {t('print.fitContent')}</div>
                      <div style={{ fontSize: 11, color: '#666', marginTop: 3 }}>{t('print.fitContentHelp')}</div>
                    </div>
                  </label>
                </div>

              </section>
            )}

            {/* Label Size Presets */}
            <section>
              <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#1a1a1a', fontWeight: 600, letterSpacing: '-0.3px' }}>{t('print.labelSizePresets')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                  {Object.entries(LABEL_PRESETS).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => handleLabelPreset(key as keyof typeof LABEL_PRESETS)}
                      style={{
                        padding: '14px 16px',
                        border: '1px solid #e8e8e8',
                        borderRadius: 10,
                        background: '#fff',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 500,
                        color: '#333',
                        transition: 'all 0.15s ease',
                        textAlign: 'left',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#1976d2';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(25,118,210,0.12)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e8e8e8';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </section>

              {/* Label Dimensions */}
              <section style={{ display: 'grid', gap: 16 }}>
                <h3 style={{ margin: 0, fontSize: 18, color: '#1a1a1a', fontWeight: 600, letterSpacing: '-0.3px' }}>{t('print.labelDimensions')}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: '#555' }}>{t('print.widthMm')}</span>
                    <input 
                      type="number" 
                      value={width} 
                      onChange={e => setWidth(+e.target.value)} 
                      style={{ 
                        padding: '12px 16px', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 8,
                        fontSize: 15,
                        background: '#fff',
                        outline: 'none',
                        transition: 'border 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#1976d2'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: '#555' }}>{t('print.heightMm')}</span>
                    <input 
                      type="number" 
                      value={height} 
                      onChange={e => setHeight(+e.target.value)} 
                      style={{ 
                        padding: '12px 16px', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 8,
                        fontSize: 15,
                        background: '#fff',
                        outline: 'none',
                        transition: 'border 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#1976d2'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: '#555' }}>{t('print.gapBetweenMm')}</span>
                    <input 
                      type="number" 
                      min={0}
                      value={gap} 
                      onChange={e => setGap(+e.target.value)} 
                      style={{ 
                        padding: '12px 16px', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 8,
                        fontSize: 15,
                        background: '#fff',
                        outline: 'none',
                        transition: 'border 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#1976d2'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: '#555' }}>{t('print.internalPaddingMm')}</span>
                    <input 
                      type="number" 
                      min={0}
                      step={0.5}
                      value={labelPadding} 
                      onChange={e => setLabelPadding(+e.target.value)} 
                      style={{ 
                        padding: '12px 16px', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 8,
                        fontSize: 15,
                        background: '#fff',
                        outline: 'none',
                        transition: 'border 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#1976d2'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                    />
                  </label>
                </div>
              </section>

              {/* Print Options */}
              <section style={{ display: 'grid', gap: 16 }}>
                <h3 style={{ margin: 0, fontSize: 18, color: '#1a1a1a', fontWeight: 600, letterSpacing: '-0.3px' }}>{t('print.printOptions')}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: '#555' }}>{t('print.copiesPerProduct')}</span>
                    <input 
                      type="number" 
                      min={1}
                      value={count} 
                      onChange={e => setCount(+e.target.value)} 
                      style={{ 
                        padding: '12px 16px', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 8,
                        fontSize: 15,
                        background: '#fff',
                        outline: 'none',
                        transition: 'border 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#1976d2'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: '#555' }}>{t('print.numberOfCopies')}</span>
                    <input 
                      type="number" 
                      min={1}
                      max={100}
                      value={numberOfCopies} 
                      onChange={e => setNumberOfCopies(+e.target.value)} 
                      style={{ 
                        padding: '12px 16px', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 8,
                        fontSize: 15,
                        background: '#fff',
                        outline: 'none',
                        transition: 'border 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#1976d2'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: '#555' }}>{t('print.currency')}</span>
                    <select 
                      value={currency} 
                      onChange={e => setCurrency(e.target.value)} 
                      style={{ 
                        padding: '12px 16px', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 8,
                        fontSize: 15,
                        background: '#fff',
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'border 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#1976d2'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                    >
                      <option value="Lei">Lei (MDL)</option>
                      <option value="RON">Lei (RON)</option>
                      <option value="€">Euro (€)</option>
                      <option value="$">Dollar ($)</option>
                      <option value="£">Pound (£)</option>
                      <option value="₺">Lira (₺)</option>
                      <option value="¥">Yen (¥)</option>
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: '#555' }}>{t('print.startPosition')}</span>
                    <input 
                      type="number" 
                      min={0}
                      value={offset} 
                      onChange={e => setOffset(+e.target.value)}
                      placeholder={t('print.skipLabels')}
                      style={{ 
                        padding: '12px 16px', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 8,
                        fontSize: 15,
                        background: '#fff',
                        outline: 'none',
                        transition: 'border 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#1976d2'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                    />
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 10, 
                    cursor: 'pointer',
                    padding: '10px 14px',
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    background: rotate ? '#e3f2fd' : '#fff',
                    transition: 'all 0.2s'
                  }}>
                    <input 
                      type="checkbox" 
                      checked={rotate} 
                      onChange={e => setRotate(e.target.checked)}
                      style={{ width: 18, height: 18, cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 14, fontWeight: 500, color: rotate ? '#1976d2' : '#333' }}>{t('print.rotate90')}</span>
                  </label>
                </div>
              </section>

              {/* Label Template Constructor */}
              <section>
                <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#1a1a1a', fontWeight: 600, letterSpacing: '-0.3px' }}>{t('print.labelTemplateConstructor')}</h3>
                
                {labelSections.map((section, sectionIndex) => (
                  <div
                    key={section.id}
                    draggable
                    onDragStart={() => { dragSectionIdx.current = sectionIndex; }}
                    onDragOver={e => {
                      e.preventDefault();
                      dragOverSectionIdx.current = sectionIndex;
                      setDragOverSection(sectionIndex);
                    }}
                    onDragLeave={() => { setDragOverSection(null); }}
                    onDrop={() => {
                      const from = dragSectionIdx.current;
                      const to = dragOverSectionIdx.current;
                      if (from !== null && to !== null && from !== to) {
                        const ns = [...labelSections];
                        const [moved] = ns.splice(from, 1);
                        ns.splice(to, 0, moved);
                        setLabelSections(ns);
                      }
                      dragSectionIdx.current = null;
                      dragOverSectionIdx.current = null;
                      setDragOverSection(null);
                    }}
                    onDragEnd={() => { dragSectionIdx.current = null; setDragOverSection(null); }}
                    style={{ 
                      marginBottom: 24, 
                      padding: 20, 
                      border: dragOverSection === sectionIndex ? '2px dashed #1976d2' : '1px solid #e8e8e8', 
                      borderRadius: 12,
                      background: section.enabled ? '#fff' : '#f5f5f5',
                      cursor: 'grab',
                      opacity: dragSectionIdx.current === sectionIndex ? 0.5 : 1
                    }}>
                    {/* Section Header with Enable Toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
                      <span
                        title={t('print.dragSection')}
                        style={{ cursor: 'grab', fontSize: 18, color: '#bdbdbd', padding: '0 4px', userSelect: 'none', lineHeight: 1 }}
                      >⠿</span>
                      <input
                        type="text"
                        value={section.name}
                        onChange={e => {
                          const ns = [...labelSections];
                          ns[sectionIndex] = { ...ns[sectionIndex], name: e.target.value };
                          setLabelSections(ns);
                        }}
                        style={{ flex: 1, fontSize: 15, fontWeight: 600, border: '1px solid #ddd', borderRadius: 6, padding: '5px 8px', color: '#1a1a1a', background: '#fafafa' }}
                      />
                      <button
                        onClick={() => setLabelSections(labelSections.filter((_, i) => i !== sectionIndex))}
                        title={t('print.deleteSection')}
                        style={{ padding: '5px 9px', border: '1px solid #ffcccc', borderRadius: 5, cursor: 'pointer', background: '#fff5f5', color: '#d32f2f', fontSize: 14 }}
                      >✕</button>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginLeft: 4 }}>
                        <span style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>{t('print.enabled')}</span>
                        <input
                          type="checkbox"
                          checked={section.enabled}
                          onChange={e => {
                            const newSections = [...labelSections];
                            newSections[sectionIndex].enabled = e.target.checked;
                            setLabelSections(newSections);
                          }}
                          style={{ width: 18, height: 18, cursor: 'pointer' }}
                        />
                      </label>
                    </div>

                    {/* Content Fields Selection */}
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, color: '#333', display: 'block', marginBottom: 8 }}>
                        {t('print.contentFields')}
                      </label>
                      {/* Checkboxes to add/remove fields */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                        {['category', 'model', 'firm', 'size', 'price', 'barcode', 'qr', 'headerText'].map(field => (
                          <label key={field} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 6, 
                            cursor: 'pointer',
                            padding: '5px 9px',
                            border: '1px solid #e8e8e8',
                            borderRadius: 6,
                            background: section.content.includes(field) ? '#e3f2fd' : '#fff',
                            fontSize: 12,
                            transition: 'all 0.2s'
                          }}>
                            <input 
                              type="checkbox" 
                              checked={section.content.includes(field)}
                              onChange={e => {
                                const newSections = [...labelSections];
                                if (e.target.checked) {
                                  newSections[sectionIndex].content = [...newSections[sectionIndex].content, field];
                                } else {
                                  newSections[sectionIndex].content = newSections[sectionIndex].content.filter(f => f !== field);
                                }
                                setLabelSections(newSections);
                              }}
                              style={{ width: 14, height: 14, cursor: 'pointer' }}
                              disabled={!section.enabled}
                            />
                            <span style={{ fontWeight: 500, color: section.content.includes(field) ? '#1976d2' : '#666' }}>
                              {t(`print.fields.${field}`)}
                            </span>
                          </label>
                        ))}
                      </div>
                      {/* Active fields in current order — drag to reorder */}
                      {section.content.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, padding: '8px 10px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                          <span style={{ fontSize: 11, color: '#999', marginRight: 4 }}>{t('print.orderDrag')}</span>
                          {section.content.map((field, fieldIdx) => (
                            <div
                              key={`${field}-${fieldIdx}`}
                              draggable
                              onDragStart={e => {
                                e.stopPropagation();
                                dragFieldKey.current = { sectionIdx: sectionIndex, fieldIdx };
                              }}
                              onDragOver={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                dragOverFieldIdx.current = fieldIdx;
                                setDragOverField({ sectionIdx: sectionIndex, fieldIdx });
                              }}
                              onDragLeave={() => setDragOverField(null)}
                              onDrop={e => {
                                e.stopPropagation();
                                const src = dragFieldKey.current;
                                const toIdx = dragOverFieldIdx.current;
                                if (src && src.sectionIdx === sectionIndex && toIdx !== null && src.fieldIdx !== toIdx) {
                                  const ns = [...labelSections];
                                  const c = [...ns[sectionIndex].content];
                                  const [moved] = c.splice(src.fieldIdx, 1);
                                  c.splice(toIdx, 0, moved);
                                  ns[sectionIndex].content = c;
                                  setLabelSections(ns);
                                }
                                dragFieldKey.current = null;
                                dragOverFieldIdx.current = null;
                                setDragOverField(null);
                              }}
                              onDragEnd={() => { dragFieldKey.current = null; setDragOverField(null); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 3,
                                background: dragOverField?.sectionIdx === sectionIndex && dragOverField?.fieldIdx === fieldIdx ? '#bbdefb' : '#fff',
                                border: dragOverField?.sectionIdx === sectionIndex && dragOverField?.fieldIdx === fieldIdx ? '2px dashed #1976d2' : '1px solid #d0e8fd',
                                borderRadius: 5, padding: '3px 7px',
                                cursor: 'grab', fontSize: 12, fontWeight: 500, color: '#1565c0',
                                userSelect: 'none'
                              }}
                            >
                              <span style={{ color: '#90caf9', fontSize: 11 }}>⠿</span>
                              {field === 'headerText' ? 'Header' : field.charAt(0).toUpperCase() + field.slice(1)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Typography Settings Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                      {/* Font Family */}
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{t('print.fontFamily')}</span>
                        <select
                          value={section.fontFamily}
                          onChange={e => {
                            const newSections = [...labelSections];
                            newSections[sectionIndex].fontFamily = e.target.value;
                            setLabelSections(newSections);
                          }}
                          disabled={!section.enabled}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e0e0e0',
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="Arial">Arial</option>
                          <option value="Times New Roman">Times New Roman</option>
                          <option value="Courier New">Courier New</option>
                          <option value="Georgia">Georgia</option>
                          <option value="Verdana">Verdana</option>
                          <option value="Helvetica">Helvetica</option>
                          <option value="Trebuchet MS">Trebuchet MS</option>
                          <option value="Comic Sans MS">Comic Sans MS</option>
                        </select>
                      </label>

                      {/* Font Size */}
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{t('print.fontSizeMm')}</span>
                        <input
                          type="number"
                          step="0.1"
                          min="0.5"
                          max="20"
                          value={section.fontSize}
                          onChange={e => {
                            const newSections = [...labelSections];
                            newSections[sectionIndex].fontSize = parseFloat(e.target.value) || 1;
                            setLabelSections(newSections);
                          }}
                          disabled={!section.enabled}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e0e0e0',
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }}
                        />
                      </label>

                      {/* Font Weight */}
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{t('print.fontWeight')}</span>
                        <select
                          value={section.fontWeight}
                          onChange={e => {
                            const newSections = [...labelSections];
                            newSections[sectionIndex].fontWeight = e.target.value as any;
                            setLabelSections(newSections);
                          }}
                          disabled={!section.enabled}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e0e0e0',
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="normal">{t('print.weightNormal')} (400)</option>
                          <option value="500">{t('print.weightMedium')} (500)</option>
                          <option value="600">{t('print.weightSemiBold')} (600)</option>
                          <option value="bold">{t('print.weightBold')} (700)</option>
                          <option value="800">{t('print.weightExtraBold')} (800)</option>
                          <option value="900">{t('print.weightBlack')} (900)</option>
                          <option value="100">{t('print.weightThin')} (100)</option>
                          <option value="200">{t('print.weightExtraLight')} (200)</option>
                          <option value="300">{t('print.weightLight')} (300)</option>
                        </select>
                      </label>

                      {/* Font Style */}
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{t('print.fontStyle')}</span>
                        <select
                          value={section.fontStyle}
                          onChange={e => {
                            const newSections = [...labelSections];
                            newSections[sectionIndex].fontStyle = e.target.value as 'normal' | 'italic';
                            setLabelSections(newSections);
                          }}
                          disabled={!section.enabled}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e0e0e0',
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="normal">{t('print.styleNormal')}</option>
                          <option value="italic">{t('print.styleItalic')}</option>
                        </select>
                      </label>

                      {/* Text Alignment */}
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{t('print.textAlign')}</span>
                        <select
                          value={section.textAlign}
                          onChange={e => {
                            const newSections = [...labelSections];
                            newSections[sectionIndex].textAlign = e.target.value as 'left' | 'center' | 'right';
                            setLabelSections(newSections);
                          }}
                          disabled={!section.enabled}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e0e0e0',
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="left">{t('print.alignLeft')}</option>
                          <option value="center">{t('print.alignCenter')}</option>
                          <option value="right">{t('print.alignRight')}</option>
                        </select>
                      </label>

                      {/* Padding */}
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{t('print.paddingMm')}</span>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={section.padding}
                          onChange={e => {
                            const newSections = [...labelSections];
                            newSections[sectionIndex].padding = parseFloat(e.target.value) || 0;
                            setLabelSections(newSections);
                          }}
                          disabled={!section.enabled}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e0e0e0',
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }}
                        />
                      </label>

                      {/* Gap between items */}
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{t('print.itemGapMm')}</span>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          max="20"
                          value={section.gap}
                          onChange={e => {
                            const newSections = [...labelSections];
                            newSections[sectionIndex].gap = parseFloat(e.target.value) || 0;
                            setLabelSections(newSections);
                          }}
                          disabled={!section.enabled}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e0e0e0',
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }}
                        />
                      </label>
                    </div>

                    {/* Display Settings */}
                    <div style={{ marginTop: 12, marginBottom: 12 }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 8, 
                        cursor: 'pointer',
                        padding: '10px 12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: 6,
                        background: section.showFieldLabels ? '#e3f2fd' : '#fff',
                        transition: 'all 0.2s'
                      }}>
                        <input 
                          type="checkbox" 
                          checked={section.showFieldLabels}
                          onChange={e => {
                            const newSections = [...labelSections];
                            newSections[sectionIndex].showFieldLabels = e.target.checked;
                            setLabelSections(newSections);
                          }}
                          disabled={!section.enabled}
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: 13, fontWeight: 600, color: section.showFieldLabels ? '#1976d2' : '#333' }}>
                          Show Field Labels (e.g., "Model:", "Size:")
                        </span>
                      </label>
                    </div>

                    {/* Color Settings */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                      {/* Text Color */}
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{t('print.textColor')}</span>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input
                            type="color"
                            value={section.color}
                            onChange={e => {
                              const newSections = [...labelSections];
                              newSections[sectionIndex].color = e.target.value;
                              setLabelSections(newSections);
                            }}
                            disabled={!section.enabled}
                            style={{
                              width: 50,
                              height: 38,
                              border: '1px solid #e0e0e0',
                              borderRadius: 6,
                              cursor: 'pointer'
                            }}
                          />
                          <input
                            type="text"
                            value={section.color}
                            onChange={e => {
                              const newSections = [...labelSections];
                              newSections[sectionIndex].color = e.target.value;
                              setLabelSections(newSections);
                            }}
                            disabled={!section.enabled}
                            placeholder="#000000"
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              border: '1px solid #e0e0e0',
                              borderRadius: 6,
                              fontSize: 14,
                              background: '#fff'
                            }}
                          />
                        </div>
                      </label>

                      {/* Background Color */}
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{t('print.backgroundColor')}</span>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input
                            type="color"
                            value={section.backgroundColor === 'transparent' ? '#ffffff' : section.backgroundColor}
                            onChange={e => {
                              const newSections = [...labelSections];
                              newSections[sectionIndex].backgroundColor = e.target.value;
                              setLabelSections(newSections);
                            }}
                            disabled={!section.enabled}
                            style={{
                              width: 50,
                              height: 38,
                              border: '1px solid #e0e0e0',
                              borderRadius: 6,
                              cursor: 'pointer'
                            }}
                          />
                          <input
                            type="text"
                            value={section.backgroundColor}
                            onChange={e => {
                              const newSections = [...labelSections];
                              newSections[sectionIndex].backgroundColor = e.target.value;
                              setLabelSections(newSections);
                            }}
                            disabled={!section.enabled}
                            placeholder={t('print.transparentColorPlaceholder')}
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              border: '1px solid #e0e0e0',
                              borderRadius: 6,
                              fontSize: 14,
                              background: '#fff'
                            }}
                          />
                        </div>
                      </label>
                    </div>
                  </div>
                ))}

                {/* Add Section Button */}
                <button
                  onClick={() => {
                    const newId = `section_${Date.now()}`;
                    setLabelSections([...labelSections, {
                      id: newId,
                      name: 'New Section',
                      content: [],
                      fontSize: 2,
                      fontFamily: 'Arial',
                      fontWeight: '400' as const,
                      fontStyle: 'normal' as const,
                      textAlign: 'center' as const,
                      color: '#333333',
                      backgroundColor: 'transparent',
                      padding: 0.5,
                      gap: 2,
                      enabled: true,
                      showFieldLabels: false
                    }]);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px dashed #b0bec5',
                    borderRadius: 8,
                    background: '#f9fafb',
                    color: '#546e7a',
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: 'pointer',
                    marginBottom: 8
                  }}
                >
                  + Add Section
                </button>
              </section>

              {/* Page Configuration */}
              <section>
                <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#1a1a1a', fontWeight: 600, letterSpacing: '-0.3px' }}>{t('print.pageConfiguration')}</h3>
                
                <div style={{ display: 'grid', gap: 16 }}>
                  {/* Page Size Dropdown */}
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{t('print.pageSize')}</span>
                    <select 
                      value={pageSize}
                      onChange={(e) => {
                        const size = e.target.value;
                        setPageSize(size);
                        if (size !== 'custom') {
                          const preset = PAGE_PRESETS[size as keyof typeof PAGE_PRESETS];
                          if (preset) {
                            // For paper sizes, don't set width/height (they're for labels)
                            // Page size will be handled by CSS @page rules
                          }
                        }
                      }}
                      style={{
                        padding: '12px 16px',
                        fontSize: 14,
                        border: '1px solid #ddd',
                        borderRadius: 8,
                        background: '#fff',
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                    >
                      <option value="a4-portrait">{t('print.a4Portrait')} (210×297mm)</option>
                      <option value="a4-landscape">{t('print.a4Landscape')} (297×210mm)</option>
                      <option value="letter-portrait">{t('print.letterPortrait')} (216×279mm)</option>
                      <option value="letter-landscape">{t('print.letterLandscape')} (279×216mm)</option>
                      <option value="custom">{t('print.customSize')}</option>
                    </select>
                  </label>

                  {/* Custom Page Size Inputs - Show when custom is selected */}
                  {pageSize === 'custom' && (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: 12,
                      padding: 16,
                      background: '#f9fafb',
                      borderRadius: 8,
                      border: '1px solid #e5e7eb'
                    }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>{t('print.widthMm')}</span>
                        <input 
                          type="number" 
                          min={10}
                          step={1}
                          defaultValue={210}
                          style={{ 
                            padding: '10px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }} 
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>{t('print.heightMm')}</span>
                        <input 
                          type="number" 
                          min={10}
                          step={1}
                          defaultValue={297}
                          style={{ 
                            padding: '10px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }} 
                        />
                      </label>
                    </div>
                  )}

                  {/* Page Margins */}
                  <div>
                    <span style={{ 
                      display: 'block',
                      fontSize: 14, 
                      fontWeight: 600, 
                      color: '#333',
                      marginBottom: 12
                    }}>{t('print.pageMarginsMm')}</span>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: 12
                    }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>{t('print.top')}</span>
                        <input 
                          type="number" 
                          min={-100}
                          step={0.5}
                          value={pageMarginTop} 
                          onChange={e => setPageMarginTop(+e.target.value)} 
                          style={{ 
                            padding: '10px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }} 
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>{t('print.right')}</span>
                        <input 
                          type="number" 
                          min={-100}
                          step={0.5}
                          value={pageMarginRight} 
                          onChange={e => setPageMarginRight(+e.target.value)} 
                          style={{ 
                            padding: '10px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }} 
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>{t('print.bottom')}</span>
                        <input 
                          type="number" 
                          min={-100}
                          step={0.5}
                          value={pageMarginBottom} 
                          onChange={e => setPageMarginBottom(+e.target.value)} 
                          style={{ 
                            padding: '10px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }} 
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>{t('print.left')}</span>
                        <input 
                          type="number" 
                          min={-100}
                          step={0.5}
                          value={pageMarginLeft} 
                          onChange={e => setPageMarginLeft(+e.target.value)} 
                          style={{ 
                            padding: '10px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }} 
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          {false && (
            <div style={{ display: 'grid', gap: 24 }}>
              {/* Page Size with Categories */}
              <section>
                <h3 style={{ margin: '0 0 20px', fontSize: 18, color: '#1a1a1a', fontWeight: 600, letterSpacing: '-0.3px' }}>Page & Paper Size</h3>
                
                {/* Standard Paper Sizes */}
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#666', fontWeight: 600, textTransform: 'uppercase' }}>📄 Standard Paper</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                    {Object.entries(PAGE_PRESETS)
                      .filter(([_, preset]) => preset.type === 'paper')
                      .map(([key, preset]) => (
                        <button
                          key={key}
                          onClick={() => handlePagePreset(key as keyof typeof PAGE_PRESETS)}
                          style={{
                            padding: '10px 12px',
                            border: pageSize === key ? '2px solid #1976d2' : '2px solid #e0e0e0',
                            borderRadius: 8,
                            background: pageSize === key ? '#e3f2fd' : '#fafafa',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 500,
                            transition: 'all 0.2s',
                            textAlign: 'left',
                            color: pageSize === key ? '#1976d2' : '#333'
                          }}
                        >
                          {preset.name}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Check/Payment Sizes */}
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#666', fontWeight: 600, textTransform: 'uppercase' }}>💳 Check & Payment</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                    {Object.entries(PAGE_PRESETS)
                      .filter(([_, preset]) => preset.type === 'check')
                      .map(([key, preset]) => (
                        <button
                          key={key}
                          onClick={() => handlePagePreset(key as keyof typeof PAGE_PRESETS)}
                          style={{
                            padding: '10px 12px',
                            border: pageSize === key ? '2px solid #1976d2' : '2px solid #e0e0e0',
                            borderRadius: 8,
                            background: pageSize === key ? '#e3f2fd' : '#fafafa',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 500,
                            transition: 'all 0.2s',
                            textAlign: 'left',
                            color: pageSize === key ? '#1976d2' : '#333'
                          }}
                        >
                          {preset.name}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Ticket/Receipt Sizes */}
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#666', fontWeight: 600, textTransform: 'uppercase' }}>🎫 Ticket & Receipt</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                    {Object.entries(PAGE_PRESETS)
                      .filter(([_, preset]) => preset.type === 'ticket')
                      .map(([key, preset]) => (
                        <button
                          key={key}
                          onClick={() => handlePagePreset(key as keyof typeof PAGE_PRESETS)}
                          style={{
                            padding: '10px 12px',
                            border: pageSize === key ? '2px solid #1976d2' : '2px solid #e0e0e0',
                            borderRadius: 8,
                            background: pageSize === key ? '#e3f2fd' : '#fafafa',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 500,
                            transition: 'all 0.2s',
                            textAlign: 'left',
                            color: pageSize === key ? '#1976d2' : '#333'
                          }}
                        >
                          {preset.name}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Card/Single Cell Sizes */}
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#666', fontWeight: 600, textTransform: 'uppercase' }}>🃏 Card & Single Label</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                    {Object.entries(PAGE_PRESETS)
                      .filter(([_, preset]) => preset.type === 'card')
                      .map(([key, preset]) => (
                        <button
                          key={key}
                          onClick={() => handlePagePreset(key as keyof typeof PAGE_PRESETS)}
                          style={{
                            padding: '10px 12px',
                            border: pageSize === key ? '2px solid #1976d2' : '2px solid #e0e0e0',
                            borderRadius: 8,
                            background: pageSize === key ? '#e3f2fd' : '#fafafa',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 500,
                            transition: 'all 0.2s',
                            textAlign: 'left',
                            color: pageSize === key ? '#1976d2' : '#333'
                          }}
                        >
                          {preset.name}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Shipping Labels */}
                <div>
                  <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#666', fontWeight: 600, textTransform: 'uppercase' }}>📦 Shipping Labels</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                    {Object.entries(PAGE_PRESETS)
                      .filter(([_, preset]) => preset.type === 'shipping')
                      .map(([key, preset]) => (
                        <button
                          key={key}
                          onClick={() => handlePagePreset(key as keyof typeof PAGE_PRESETS)}
                          style={{
                            padding: '10px 12px',
                            border: pageSize === key ? '2px solid #1976d2' : '2px solid #e0e0e0',
                            borderRadius: 8,
                            background: pageSize === key ? '#e3f2fd' : '#fafafa',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 500,
                            transition: 'all 0.2s',
                            textAlign: 'left',
                            color: pageSize === key ? '#1976d2' : '#333'
                          }}
                        >
                          {preset.name}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Selected Size Info */}
                {pageSize && PAGE_PRESETS[pageSize as keyof typeof PAGE_PRESETS] && (
                  <div style={{ 
                    marginTop: 12, 
                    padding: 12, 
                    background: '#e3f2fd', 
                    borderRadius: 6,
                    border: '1px solid #90caf9',
                    fontSize: 13,
                    color: '#1565c0'
                  }}>
                    <strong>Selected:</strong> {PAGE_PRESETS[pageSize as keyof typeof PAGE_PRESETS].name} 
                    {' '}({PAGE_PRESETS[pageSize as keyof typeof PAGE_PRESETS].width}×{PAGE_PRESETS[pageSize as keyof typeof PAGE_PRESETS].height}mm)
                    {singleCellMode && <span style={{ marginLeft: 8, background: '#4caf50', color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>SINGLE ITEM MODE</span>}
                  </div>
                )}

                {/* Custom Page Size Editor */}
                {pageSize === 'CUSTOM' && (
                  <div style={{ 
                    marginTop: 12, 
                    padding: 16, 
                    background: '#fff3e0', 
                    borderRadius: 8,
                    border: '2px solid #ff9800'
                  }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#e65100', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>📐</span> Custom Page Dimensions
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>
                          Width (mm)
                        </label>
                        <input 
                          type="number" 
                          min="10" 
                          max="1000" 
                          value={customPageWidth} 
                          onChange={(e) => setCustomPageWidth(Number(e.target.value))}
                          style={{ 
                            width: '100%', 
                            padding: 8, 
                            border: '1px solid #ddd', 
                            borderRadius: 4, 
                            fontSize: 14 
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>
                          Height (mm)
                        </label>
                        <input 
                          type="number" 
                          min="10" 
                          max="1000" 
                          value={customPageHeight} 
                          onChange={(e) => setCustomPageHeight(Number(e.target.value))}
                          style={{ 
                            width: '100%', 
                            padding: 8, 
                            border: '1px solid #ddd', 
                            borderRadius: 4, 
                            fontSize: 14 
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 11, color: '#666', fontStyle: 'italic' }}>
                      Enter custom dimensions between 10mm and 1000mm
                    </div>
                  </div>
                )}
              </section>

              {/* Single Cell Mode Toggle */}
              <section style={{ padding: 16, background: singleCellMode ? '#e8f5e9' : '#f5f5f5', borderRadius: 8, border: singleCellMode ? '2px solid #4caf50' : '2px solid #e0e0e0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                  <input 
                    type="checkbox" 
                    checked={singleCellMode} 
                    onChange={toggleSingleCellMode}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: singleCellMode ? '#2e7d32' : '#333' }}>
                      📄 Single Item Per Page Mode
                    </div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      Perfect for checks, tickets, cards, or receipts. Automatically adjusts layout to print one item per page.
                    </div>
                  </div>
                </label>
              </section>

              {/* Page Margins */}
              <section style={{ display: 'grid', gap: 12, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0, fontSize: 16, color: '#333', fontWeight: 600 }}>Page Margins (mm)</h3>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(['none', 'narrow', 'normal', 'wide'] as const).map(preset => (
                      <button
                        key={preset}
                        onClick={() => handleMarginPreset(preset)}
                        style={{
                          padding: '4px 10px',
                          border: '1px solid #ddd',
                          borderRadius: 4,
                          background: '#fff',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>Top</span>
                    <input 
                      type="number" 
                      min={0}
                      step={0.5}
                      value={pageMarginTop} 
                      onChange={e => setPageMarginTop(+e.target.value)} 
                      style={{ 
                        padding: '8px 12px', 
                        border: '1px solid #ddd', 
                        borderRadius: 6,
                        fontSize: 14,
                        background: '#fff'
                      }} 
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>Right</span>
                    <input 
                      type="number" 
                      min={0}
                      step={0.5}
                      value={pageMarginRight} 
                      onChange={e => setPageMarginRight(+e.target.value)} 
                      style={{ 
                        padding: '8px 12px', 
                        border: '1px solid #ddd', 
                        borderRadius: 6,
                        fontSize: 14,
                        background: '#fff'
                      }} 
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>Bottom</span>
                    <input 
                      type="number" 
                      min={0}
                      step={0.5}
                      value={pageMarginBottom} 
                      onChange={e => setPageMarginBottom(+e.target.value)} 
                      style={{ 
                        padding: '8px 12px', 
                        border: '1px solid #ddd', 
                        borderRadius: 6,
                        fontSize: 14,
                        background: '#fff'
                      }} 
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>Left</span>
                    <input 
                      type="number" 
                      min={0}
                      step={0.5}
                      value={pageMarginLeft} 
                      onChange={e => setPageMarginLeft(+e.target.value)} 
                      style={{ 
                        padding: '8px 12px', 
                        border: '1px solid #ddd', 
                        borderRadius: 6,
                        fontSize: 14,
                        background: '#fff'
                      }} 
                    />
                  </label>
                </div>
              </section>

              {/* Auto-Calculate Toggle */}
              {!singleCellMode && (
                <section style={{ padding: 16, background: '#e8eaf6', borderRadius: 8, border: '2px solid #7986cb' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={autoCalculate} 
                      onChange={(e) => setAutoCalculate(e.target.checked)}
                      style={{ width: 18, height: 18, cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#283593' }}>
                        🔄 Smart Auto-Calculate Layout
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        Automatically synchronize cell size and columns/rows. Change cell size → updates columns/rows. Change columns/rows → updates cell size.
                      </div>
                    </div>
                  </label>
                </section>
              )}

              {/* Table Layout - Only show if NOT in single cell mode */}
              {!singleCellMode && (
                <>
                  <section style={{ display: 'grid', gap: 12, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
                    <h3 style={{ margin: 0, fontSize: 16, color: '#333', fontWeight: 600 }}>Table Layout</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>Number of Columns</span>
                        <input 
                          type="number" 
                          min={1}
                          max={12}
                          value={columns} 
                          onChange={e => handleColumnsChange(+e.target.value)} 
                          style={{ 
                            padding: '8px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }} 
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>Number of Rows</span>
                        <input 
                          type="number" 
                          min={1}
                          max={20}
                          value={rows} 
                          onChange={e => handleRowsChange(+e.target.value)} 
                          style={{ 
                            padding: '8px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }} 
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>Container Padding (px)</span>
                        <input 
                          type="number" 
                          min={0}
                          value={containerPadding} 
                          onChange={e => setContainerPadding(+e.target.value)} 
                          style={{ 
                            padding: '8px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }} 
                        />
                      </label>
                    </div>
                  </section>

                  {/* Cell Sizing */}
                  <section style={{ display: 'grid', gap: 12, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 style={{ margin: 0, fontSize: 16, color: '#333', fontWeight: 600 }}>Cell Dimensions</h3>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                        <input 
                          type="checkbox" 
                          checked={useMmInput} 
                          onChange={e => setUseMmInput(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ color: '#555', fontWeight: 500 }}>Use mm input</span>
                      </label>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>
                          Cell Width {useMmInput ? '(mm)' : `(px ≈ ${pxToMm(cellWidth)} mm)`}
                        </span>
                        <input 
                          type="number" 
                          min={useMmInput ? 13 : 50}
                          step={useMmInput ? 0.1 : 1}
                          value={useMmInput ? pxToMm(cellWidth) : cellWidth} 
                          onChange={e => handleCellWidthChange(useMmInput ? mmToPx(+e.target.value) : +e.target.value)} 
                          style={{ 
                            padding: '8px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }} 
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>
                          Cell Height {useMmInput ? '(mm)' : `(px ≈ ${pxToMm(cellHeight)} mm)`}
                        </span>
                        <input 
                          type="number" 
                          min={useMmInput ? 5 : 20}
                          step={useMmInput ? 0.1 : 1}
                          value={useMmInput ? pxToMm(cellHeight) : cellHeight} 
                          onChange={e => handleCellHeightChange(useMmInput ? mmToPx(+e.target.value) : +e.target.value)} 
                          style={{ 
                            padding: '8px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }} 
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>
                          Cell Padding {useMmInput ? '(mm)' : `(px ≈ ${pxToMm(cellPadding)} mm)`}
                        </span>
                        <input 
                          type="number" 
                          min={0}
                          step={useMmInput ? 0.1 : 1}
                          value={useMmInput ? pxToMm(cellPadding) : cellPadding} 
                          onChange={e => setCellPadding(useMmInput ? mmToPx(+e.target.value) : +e.target.value)} 
                          style={{ 
                            padding: '8px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: 6,
                            fontSize: 14,
                            background: '#fff'
                          }} 
                        />
                      </label>
                    </div>
                    
                    {/* Separate Gap Controls */}
                    <div style={{ marginTop: 8 }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#555', fontWeight: 600 }}>{t('print.cellSpacing')}</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>{t('print.horizontalGap')} ({pxToMm(cellHorizontalGap)} mm)</span>
                          <input 
                            type="number" 
                            min={0}
                            value={cellHorizontalGap} 
                            onChange={e => handleHorizontalGapChange(+e.target.value)} 
                            style={{ 
                              padding: '8px 12px', 
                              border: '1px solid #ddd', 
                              borderRadius: 6,
                              fontSize: 14,
                              background: '#fff'
                            }} 
                          />
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>{t('print.verticalGap')} ({pxToMm(cellVerticalGap)} mm)</span>
                          <input 
                            type="number" 
                            min={0}
                            value={cellVerticalGap} 
                            onChange={e => handleVerticalGapChange(+e.target.value)} 
                            style={{ 
                              padding: '8px 12px', 
                              border: '1px solid #ddd', 
                              borderRadius: 6,
                              fontSize: 14,
                              background: '#fff'
                            }} 
                          />
                        </label>
                      </div>
                    </div>

                    {/* Layout Preview */}
                    {autoCalculate && (
                      <div style={{ 
                        marginTop: 12, 
                        padding: 12, 
                        background: '#e8f5e9', 
                        borderRadius: 6,
                        border: '1px solid #4caf50',
                        fontSize: 13,
                        color: '#2e7d32'
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>📊 Calculated Layout:</div>
                        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                          <div><strong>{columns} columns</strong> × <strong>{rows} rows</strong> = <strong>{columns * rows} cells per page</strong></div>
                          <div style={{ marginTop: 4, color: '#666' }}>
                            Printable area: {Math.round(getAvailableSpace().width)}×{Math.round(getAvailableSpace().height)} px
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Display Options for Table Layout */}
                    <div style={{ marginTop: 16 }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#555', fontWeight: 600 }}>{t('print.displayOptions')}</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input 
                            type="checkbox" 
                            checked={rotate} 
                            onChange={e => setRotate(e.target.checked)}
                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 14, color: '#333' }}>{t('print.rotate90')}</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={printCellBorder}
                            onChange={e => setPrintCellBorder(e.target.checked)}
                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 14, color: '#333' }}>Show cell borders when printing</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={printFitContent}
                            onChange={e => setPrintFitContent(e.target.checked)}
                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                          />
                          <div>
                            <div style={{ fontSize: 14, color: '#333' }}>Scale content to fit cell</div>
                            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                              Shrinks text/images to stay inside each cell; unchecked lets cells expand to content
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </section>
                </>
              )}
            </div>
          )}
        </div>
        )}

        {/* Footer Actions */}
        <div style={{ 
          padding: '24px 32px', 
          borderTop: '1px solid #e8e8e8',
          display: 'flex', 
          gap: 12,
          background: '#fafafa',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.04)'
        }}>
          <button
            onClick={() => {
              // Build expanded products based on per-product copies if available
              if (onProductsConfirm && products && products.length > 0) {
                const expanded: Product[] = [];
                products.forEach(p => {
                  const copies = p.id != null ? (copiesMap[p.id] ?? 1) : 1;
                  for (let i = 0; i < copies; i++) expanded.push(p);
                });
                onProductsConfirm(expanded);
              }
              onConfirm({ 
                width, height, count: (onProductsConfirm && products && products.length > 0) ? 1 : count,
                numberOfCopies, currency, gap, showBarcode, showQR, rotate, offset,
                pageMarginTop, pageMarginRight, pageMarginBottom, pageMarginLeft, 
                containerPadding, cellPadding, labelPadding,
                columns, cellWidth, cellHeight, cellGap, pageSize,
                labelSections,
                printCellBorder, printFitContent
              });
            }}
            style={{ 
              flex: 1,
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 10, 
              padding: '14px 32px', 
              fontWeight: 600,
              fontSize: 16,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(25, 118, 210, 0.25)',
              transition: 'all 0.2s',
              letterSpacing: '0.3px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(25, 118, 210, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(25, 118, 210, 0.25)';
            }}
          >
            {t('print.applyAndPrint')}
          </button>
          {onSave && (
            <button
              onClick={async () => {
                setSaveStatus('saving');
                try {
                  await onSave({
                    width, height, count, numberOfCopies, currency, gap, showBarcode, showQR, rotate, offset,
                    pageMarginTop, pageMarginRight, pageMarginBottom, pageMarginLeft,
                    containerPadding, cellPadding, labelPadding,
                    columns, cellWidth, cellHeight, cellGap, pageSize,
                    labelSections,
                    printCellBorder, printFitContent
                  });
                  setSaveStatus('saved');
                  setTimeout(() => setSaveStatus('idle'), 2000);
                } catch {
                  setSaveStatus('error');
                  setTimeout(() => setSaveStatus('idle'), 2500);
                }
              }}
              disabled={saveStatus === 'saving'}
              style={{
                padding: '14px 24px',
                background: saveStatus === 'saved' ? '#e8f5e9' : saveStatus === 'error' ? '#ffebee' : '#fff',
                border: `1px solid ${saveStatus === 'saved' ? '#81c784' : saveStatus === 'error' ? '#e57373' : '#1976d2'}`,
                borderRadius: 10,
                fontWeight: 600,
                fontSize: 15,
                color: saveStatus === 'saved' ? '#388e3c' : saveStatus === 'error' ? '#c62828' : '#1976d2',
                cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              {saveStatus === 'saving' ? '...' : saveStatus === 'saved' ? `✓ ${t('print.saved')}` : saveStatus === 'error' ? `✕ ${t('print.error')}` : `💾 ${t('print.saveSettings')}`}
            </button>
          )}
          <button 
            onClick={onClose} 
            style={{ 
              padding: '14px 32px',
              background: '#fff', 
              border: '1px solid #d0d0d0', 
              borderRadius: 10,
              fontWeight: 600,
              fontSize: 16,
              color: '#666',
              cursor: 'pointer',
              transition: 'all 0.2s',
              letterSpacing: '0.3px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f5f5f5';
              e.currentTarget.style.borderColor = '#999';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#d0d0d0';
            }}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
});

// ---------- MAIN LIST ----------
export default function ProductList() {
  const { isActive: subscriptionActive } = useSubscriptionStatus();
  const { t } = useTranslation();
  const role = localStorage.getItem('role');

  // companies
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [adminUsers, setAdminUsers] = useState<Array<{ id: number; username: string; role: string }>>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  // data
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);

  // UI
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showHidden, setShowHidden] = useState(false);

  // filters & pagination
  const [filterName, setFilterName] = useState('');
  const [filterFirm, setFilterFirm] = useState('');
  const [filterModel, setFilterModel] = useState('');
  const [filterSize, setFilterSize] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const deferredFilterName = useDeferredValue(filterName);
  const deferredFilterFirm = useDeferredValue(filterFirm);
  const deferredFilterModel = useDeferredValue(filterModel);
  const deferredFilterSize = useDeferredValue(filterSize);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // print state
  const [printSettingsOpen, setPrintSettingsOpen] = useState(false);
  const [printAllMode, setPrintAllMode] = useState(false);
  const [printSingleProduct, setPrintSingleProduct] = useState<Product | null>(null);
  const [expandedPrintProducts, setExpandedPrintProducts] = useState<Product[] | null>(null);
  const [printArgs, setPrintArgs] = useState<{
    width: number; height: number; gap: number; count: number; numberOfCopies?: number; currency?: string; showBarcode: boolean; showQR: boolean; rotate: boolean; offset: number;
    pageMarginTop?: number; pageMarginRight?: number; pageMarginBottom?: number; pageMarginLeft?: number;
    containerPadding?: number; cellPadding?: number; labelPadding?: number;
    printCellBorder?: boolean; printFitContent?: boolean;
  } | null>(null);

  // Quick price edit state
  const [editingPriceId, setEditingPriceId] = useState<number | null>(null);
  const [editingPriceValue, setEditingPriceValue] = useState<string>('');

  // company management state
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [companyModalProduct, setCompanyModalProduct] = useState<Product | null>(null);
  const [selectedCompaniesForProduct, setSelectedCompaniesForProduct] = useState<number[]>([]);

  // Quick price edit handlers
  const handlePriceDoubleClick = (product: Product) => {
    setEditingPriceId(product.id!);
    setEditingPriceValue(product.price?.toString() || '');
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingPriceValue(e.target.value);
  };

  const handlePriceBlur = async () => {
    if (editingPriceId === null) return;
    
    const newPrice = parseFloat(editingPriceValue);
    if (isNaN(newPrice) || newPrice < 0) {
      alert(t('productList.enterValidPrice'));
      setEditingPriceId(null);
      return;
    }

    const product = products.find(p => p.id === editingPriceId);
    if (!product) {
      setEditingPriceId(null);
      return;
    }

    try {
      await ProductApi.update(editingPriceId, {
        ...product,
        price: newPrice
      });
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === editingPriceId ? { ...p, price: newPrice } : p
      ));
      
      setEditingPriceId(null);
    } catch (error) {
      console.error('Failed to update price:', error);
      alert(t('productList.priceUpdateFailed'));
      setEditingPriceId(null);
    }
  };

  const handlePriceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handlePriceBlur();
    } else if (e.key === 'Escape') {
      setEditingPriceId(null);
    }
  };

  const [labelW, setLabelW] = useState(62);
  const [labelH, setLabelH] = useState(32);
  const [gap, setGap] = useState(2);
  const [count, setCount] = useState(1);
  const [showBarcode, setShowBarcode] = useState(true);
  const [showQR, setShowQR] = useState(true);
  const [rotate, setRotate] = useState(false);
  const [offset, setOffset] = useState(0);
  const [currency, setCurrency] = useState('Lei');
  
  // Padding settings for print
  const [pageMarginTop, setPageMarginTop] = useState(10);
  const [pageMarginRight, setPageMarginRight] = useState(10);
  const [pageMarginBottom, setPageMarginBottom] = useState(10);
  const [pageMarginLeft, setPageMarginLeft] = useState(10);
  const [containerPadding, setContainerPadding] = useState(16);
  const [cellPadding, setCellPadding] = useState(8);
  const [labelPadding, setLabelPadding] = useState(2);
  
  // Table layout settings
  const [columns, setColumns] = useState(4);
  const [cellWidth, setCellWidth] = useState(150);
  const [cellHeight, setCellHeight] = useState(40);
  const [cellGap, setCellGap] = useState(8);
  const [pageSize, setPageSize] = useState('A4_PORTRAIT');
  const [printCellBorder, setPrintCellBorder] = useState(true);
  const [printFitContent, setPrintFitContent] = useState(true);

  // Label Template Configuration
  const [labelSections, setLabelSections] = useState<LabelSection[]>([
    {
      id: 'category',
      name: 'Category Section',
      content: ['category'],
      fontSize: 2.5,
      fontFamily: 'Arial',
      fontWeight: '700',
      fontStyle: 'normal',
      textAlign: 'center',
      color: '#333333',
      backgroundColor: 'transparent',
      padding: 0.5,
      gap: 2,
      enabled: true,
      showFieldLabels: false
    },
    {
      id: 'top',
      name: 'Top Section',
      content: ['model', 'firm', 'size'],
      fontSize: 2,
      fontFamily: 'Arial',
      fontWeight: '500',
      fontStyle: 'normal',
      textAlign: 'center',
      color: '#555555',
      backgroundColor: 'transparent',
      padding: 0.8,
      gap: 4,
      enabled: true,
      showFieldLabels: true
    },
    {
      id: 'middle',
      name: 'Middle Section',
      content: ['price'],
      fontSize: 10,
      fontFamily: 'Arial',
      fontWeight: '900',
      fontStyle: 'normal',
      textAlign: 'center',
      color: '#000000',
      backgroundColor: 'transparent',
      padding: 1,
      gap: 5,
      enabled: true,
      showFieldLabels: false
    },
    {
      id: 'bottom',
      name: 'Bottom Section',
      content: ['barcode', 'qr', 'headerText'],
      fontSize: 1.8,
      fontFamily: 'Arial',
      fontWeight: '400',
      fontStyle: 'normal',
      textAlign: 'center',
      color: '#333333',
      backgroundColor: 'transparent',
      padding: 0.8,
      gap: 4,
      enabled: true,
      showFieldLabels: false
    }
  ]);

  // --- load admin user list for admin-only user scope selection
  useEffect(() => {
    if (role !== 'ROLE_ADMIN') return;
    AdminUserApi.getAll()
      .then(res => setAdminUsers((res.data || []).map(u => ({ id: u.id, username: u.username, role: u.role })))
      )
      .catch(() => setAdminUsers([]));
  }, [role]);

  // --- load companies (current user scope, or selected user scope for admin)
  useEffect(() => {
    (async () => {
      try {
        let companiesResponse;
        if (role === 'ROLE_ADMIN' && selectedUserId) {
          companiesResponse = await AdminUserApi.getUserCompanies(selectedUserId);
        } else {
          companiesResponse = await PersonalCabinetApi.getPersonalCompanies();
        }

        const list: Company[] = companiesResponse.data || [];
        setCompanies(list);

        const stored = localStorage.getItem('selectedCompanyId');
        const initial = (stored && list.some(c => c.id === Number(stored)))
          ? Number(stored)
          : (list[0]?.id ?? null);
        setSelectedCompanyId(initial);
      } catch {
        setCompanies([]);
        setSelectedCompanyId(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, selectedUserId]);

  // --- load categories
  useEffect(() => {
    CategoryApi.getAll().then(res => setCategories(res.data));
  }, []);

  // --- load user print settings when component mounts
  useEffect(() => {
    console.log('🔍 Loading user print settings...');
    
    UserPrintSettingsApi.get()
      .then(res => {
        const settings = res.data;
        console.log('✓ Received print settings from DB:', settings);
        // Apply label dimension settings
        if (settings.labelWidth != null) setLabelW(settings.labelWidth);
        if (settings.labelHeight != null) setLabelH(settings.labelHeight);
        if (settings.labelGap != null) setGap(settings.labelGap);
        if (settings.labelPadding != null) setLabelPadding(settings.labelPadding);
        if (settings.rotate != null) setRotate(settings.rotate);
        
        // Apply display settings
        if (settings.showBarcode != null) setShowBarcode(settings.showBarcode);
        if (settings.showQr != null) setShowQR(settings.showQr);
        if (settings.currency != null) setCurrency(settings.currency);
        
        // Apply page settings
        if (settings.pageMarginTop != null) setPageMarginTop(settings.pageMarginTop);
        if (settings.pageMarginRight != null) setPageMarginRight(settings.pageMarginRight);
        if (settings.pageMarginBottom != null) setPageMarginBottom(settings.pageMarginBottom);
        if (settings.pageMarginLeft != null) setPageMarginLeft(settings.pageMarginLeft);
        if (settings.pageSize != null) setPageSize(settings.pageSize);
        
        // Apply table layout settings
        if (settings.tableColumns != null) setColumns(settings.tableColumns);
        if (settings.tableCellWidth != null) setCellWidth(settings.tableCellWidth);
        if (settings.tableCellHeight != null) setCellHeight(settings.tableCellHeight);
        if (settings.tableCellGap != null) setCellGap(settings.tableCellGap);
        if (settings.containerPadding != null) setContainerPadding(settings.containerPadding);
        if (settings.cellPadding != null) setCellPadding(settings.cellPadding);
        
        // Apply label sections from saved settings
        if (settings.labelSectionsJson) {
          try {
            const sections = JSON.parse(settings.labelSectionsJson);
            if (Array.isArray(sections) && sections.length > 0) {
              // Migrate: add showFieldLabels default if missing from older saved data
              const migrated = sections.map((s: any) => ({
                ...s,
                showFieldLabels: s.showFieldLabels ?? (s.id === 'top')
              }));
              setLabelSections(migrated);
              console.log('✓ Applied label sections:', migrated);
            }
          } catch (err) {
            console.error('❌ Failed to parse label sections JSON:', err);
          }
        }
        
        console.log('✓ All print settings loaded and applied');
      })
      .catch(err => {
        if (err.response?.status === 404) {
          console.log('ℹ️ No saved print settings - using defaults');
        } else {
          console.error('❌ Error loading print settings:', err);
        }
      });
  }, []); // Load once on mount

  // --- fetch products when company selection or showHidden changes
  useEffect(() => {
    if (selectedCompanyId === null) return;
    let active = true;
    let requestId = 0;
    const fetchProducts = async (refresh = false) => {
      const current = ++requestId;
      try {
        if (!refresh) setLoading(true);
        let response;
        response = await CompanyApi.products(Number(selectedCompanyId), showHidden);
        if (active && current === requestId) setProducts(response.data || []);
      } catch {
        if (active && current === requestId && !refresh) setProducts([]);
      } finally {
        if (active && current === requestId) setLoading(false);
      }
    };
    const onInventoryChanged = (event: Event) => {
      if ((event as CustomEvent<InventoryChange>).detail.companyId === Number(selectedCompanyId)) void fetchProducts(true);
    };
    window.addEventListener(INVENTORY_CHANGED, onInventoryChanged);
    fetchProducts();
    localStorage.setItem('selectedCompanyId', String(selectedCompanyId));
    setPage(1);
    return () => { active = false; window.removeEventListener(INVENTORY_CHANGED, onInventoryChanged); };
  }, [selectedCompanyId, role, showHidden]);

  // beforeprint: scale table cell content down to fit when printFitContent is on
  useEffect(() => {
    const fitCells = () => {
      if (!printFitContent) return;
      const tds = document.querySelectorAll<HTMLTableCellElement>('.print-container td');
      tds.forEach(td => {
        // find or create the inner wrapper
        let inner = td.querySelector<HTMLElement>('.print-cell-inner');
        if (!inner) {
          // wrap all children
          inner = document.createElement('div');
          inner.className = 'print-cell-inner';
          inner.style.display = 'inline-block';
          inner.style.transformOrigin = 'top left';
          while (td.firstChild) inner.appendChild(td.firstChild);
          td.appendChild(inner);
        }
        // reset scale so we can measure natural size
        inner.style.transform = 'scale(1)';
        const tdW = td.clientWidth;
        const tdH = td.clientHeight;
        const natW = inner.scrollWidth;
        const natH = inner.scrollHeight;
        if (natW === 0 || natH === 0) return;
        const scale = Math.min(1, tdW / natW, tdH / natH);
        inner.style.transform = scale < 1 ? `scale(${scale.toFixed(4)})` : 'scale(1)';
      });
    };

    const cleanup = () => {
      // unwrap inner divs to restore DOM for future renders
      const inners = document.querySelectorAll<HTMLElement>('.print-cell-inner');
      inners.forEach(inner => {
        const parent = inner.parentElement;
        if (!parent) return;
        inner.style.transform = 'scale(1)';
        while (inner.firstChild) parent.insertBefore(inner.firstChild, inner);
        parent.removeChild(inner);
      });
    };

    window.addEventListener('beforeprint', fitCells);
    window.addEventListener('afterprint', cleanup);
    return () => {
      window.removeEventListener('beforeprint', fitCells);
      window.removeEventListener('afterprint', cleanup);
    };
  }, [printFitContent]);

  // derived lists
  const filteredProducts = useMemo(() => {
    if (!products.length) return [];
    
    const nameFilter = deferredFilterName.trim().toLocaleLowerCase();
    const firmFilter = deferredFilterFirm.trim().toLocaleLowerCase();
    const modelFilter = deferredFilterModel.trim().toLocaleLowerCase();
    const sizeFilter = deferredFilterSize.trim().toLocaleLowerCase();
    
    return products.filter(p => {
      if (nameFilter && !p.name.toLowerCase().includes(nameFilter)) return false;
      if (firmFilter && !(p.firm || '').toLowerCase().includes(firmFilter)) return false;
      if (modelFilter && !(p.model || '').toLowerCase().includes(modelFilter)) return false;
      if (sizeFilter && !(p.size || '').toLowerCase().includes(sizeFilter)) return false;
      if (filterCategory && (!p.category || String(p.category.id) !== filterCategory)) return false;
      return true;
    });
  }, [products, deferredFilterName, deferredFilterFirm, deferredFilterModel, deferredFilterSize, filterCategory]);

  const lastCompanyProduct = useMemo(() => {
    if (!selectedCompanyId) return undefined;
    const companyProducts = products.filter(p => p.companies?.some(c => c.id === selectedCompanyId));
    return companyProducts.length > 0
      ? companyProducts.reduce((latest, current) =>
          (current.id && latest.id && current.id > latest.id) ? current : latest)
      : undefined;
  }, [products, selectedCompanyId]);

  const paginationData = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(filteredProducts.length / rowsPerPage));
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedProducts = filteredProducts.slice(startIndex, endIndex);
    
    return { totalPages, paginatedProducts };
  }, [filteredProducts, page, rowsPerPage]);

  // helpers
  const exportToCSV = () => {
    const csvData = products.map(p => ({
      Name: p.name,
      Firm: p.firm,
      Model: p.model,
      Description: p.description,
      Size: p.size,
      Price: p.price,
      Barcode: getProductBarcode(p),
      Category: p.category?.name,
      Companies: p.companies?.map(c => c.name).join(', ') || 'No company'
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'products.csv');
  };

  const handleHideProduct = useCallback(async (id: number) => {
    if (!window.confirm(t('productList.confirmHide'))) return;
    try {
      await ProductApi.softDelete(id);
      if (selectedCompanyId) {
        const res = await CompanyApi.products(Number(selectedCompanyId), showHidden);
        setProducts(res.data);
      }
      alert(t('productList.productHidden'));
    } catch (error: any) {
      console.error('Hide failed:', error);
      const errorMessage = error.response?.data?.message || error.message || t('common.unknownError');
      alert(t('productList.productHideFailed') + ': ' + errorMessage);
    }
  }, [selectedCompanyId, showHidden, t]);

  const handleHardDeleteProduct = useCallback(async (id: number, name: string) => {
    if (!window.confirm(t('productList.confirmPermanentDelete', { name }))) return;
    try {
      await ProductApi.hardDelete(id);
      if (selectedCompanyId) {
        const res = await CompanyApi.products(Number(selectedCompanyId), showHidden);
        setProducts(res.data);
      }
      alert(t('productList.permanentlyDeleted'));
    } catch (error: any) {
      console.error('Hard delete failed:', error);
      const errorMessage = error.response?.data?.message || error.message || t('common.unknownError');
      alert(t('productList.permanentDeleteFailed') + ': ' + errorMessage);
    }
  }, [selectedCompanyId, showHidden, t]);

  const handleToggleHidden = useCallback(async (product: Product, hidden: boolean) => {
    if (!product.id) return;
    try {
      await ProductApi.setHidden(product.id, hidden);
      if (selectedCompanyId) {
        const res = await CompanyApi.products(Number(selectedCompanyId), showHidden);
        setProducts(res.data || []);
      }
    } catch (error: any) {
      console.error('Toggle hidden failed:', error);
      const errorMessage = error.response?.data?.message || error.message || t('common.unknownError');
      alert(t('productList.visibilityUpdateFailed') + ': ' + errorMessage);
    }
  }, [selectedCompanyId, showHidden, t]);

  const deleteAllProducts = async () => {
    const currentProducts = filteredProducts;
    const companyText = companies.find(c => c.id === Number(selectedCompanyId))?.name || t('productList.selectedCompany');
    if (currentProducts.length === 0) {
      alert(t('productList.noProductsToDelete'));
      return;
    }
    const confirmMessage = t('productList.confirmDeleteAllFromCompany', {
      count: currentProducts.length,
      company: companyText
    });
    if (!window.confirm(confirmMessage)) return;
    try {
      for (const product of currentProducts) {
        try { await ProductApi.softDelete(product.id!); } catch {}
      }
      const res = await CompanyApi.products(Number(selectedCompanyId), showHidden);
      setProducts(res.data);
      alert(t('productList.deleteCompleted'));
    } catch {
      alert(t('productList.deleteFailed'));
    }
  };

  // company management functions
  const handleOpenCompanyModal = useCallback((product: Product) => {
    setCompanyModalProduct(product);
    setSelectedCompaniesForProduct(product.companies?.map(c => c.id) || []);
    setCompanyModalOpen(true);
  }, []);

  const closeCompanyManagementModal = () => {
    setCompanyModalOpen(false);
    setCompanyModalProduct(null);
    setSelectedCompaniesForProduct([]);
  };

  const toggleCompanyForProduct = (companyId: number) => {
    setSelectedCompaniesForProduct(prev =>
      prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]
    );
  };

  const updateProductCompanies = async () => {
    if (!companyModalProduct) return;
    try {
      const updatedProduct = { ...companyModalProduct, companies: companies.filter(c => selectedCompaniesForProduct.includes(c.id)) };
      await ProductApi.update(companyModalProduct.id!, updatedProduct);
      const refreshed = await CompanyApi.products(Number(selectedCompanyId), showHidden);
      setProducts(refreshed.data);
      alert(t('productList.productUpdated'));
      closeCompanyManagementModal();
    } catch {
      alert(t('productList.productUpdateFailed'));
    }
  };

  // printing flows
  function openSettings({ all, product }: { all: boolean; product?: Product }) {
    setPrintAllMode(all);
    setPrintSingleProduct(product ?? null);
    setPrintSettingsOpen(true);
  }

  async function saveSettingsOnly(s: {
    width: number; height: number; count: number; numberOfCopies?: number; currency?: string; gap: number;
    showBarcode: boolean; showQR: boolean; rotate: boolean; offset: number;
    pageMarginTop?: number; pageMarginRight?: number; pageMarginBottom?: number; pageMarginLeft?: number;
    containerPadding?: number; cellPadding?: number; labelPadding?: number;
    columns?: number; cellWidth?: number; cellHeight?: number; cellGap?: number;
    pageSize?: string;
    labelSections?: LabelSection[];
    printCellBorder?: boolean;
    printFitContent?: boolean;
  }) {
    // Apply state so the live preview reflects the change
    setLabelW(s.width); setLabelH(s.height); setGap(s.gap); setCount(s.count);
    setShowBarcode(s.showBarcode); setShowQR(s.showQR); setRotate(s.rotate); setOffset(s.offset);
    if (s.currency !== undefined) setCurrency(s.currency);
    if (s.pageMarginTop !== undefined) setPageMarginTop(s.pageMarginTop);
    if (s.pageMarginRight !== undefined) setPageMarginRight(s.pageMarginRight);
    if (s.pageMarginBottom !== undefined) setPageMarginBottom(s.pageMarginBottom);
    if (s.pageMarginLeft !== undefined) setPageMarginLeft(s.pageMarginLeft);
    if (s.containerPadding !== undefined) setContainerPadding(s.containerPadding);
    if (s.cellPadding !== undefined) setCellPadding(s.cellPadding);
    if (s.labelPadding !== undefined) setLabelPadding(s.labelPadding);
    if (s.columns !== undefined) setColumns(s.columns);
    if (s.cellWidth !== undefined) setCellWidth(s.cellWidth);
    if (s.cellHeight !== undefined) setCellHeight(s.cellHeight);
    if (s.cellGap !== undefined) setCellGap(s.cellGap);
    if (s.pageSize !== undefined) setPageSize(s.pageSize);
    if (s.printCellBorder !== undefined) setPrintCellBorder(s.printCellBorder);
    if (s.printFitContent !== undefined) setPrintFitContent(s.printFitContent);
    if (s.labelSections !== undefined) setLabelSections(s.labelSections);

    await UserPrintSettingsApi.save({
      labelWidth: s.width,
      labelHeight: s.height,
      labelGap: s.gap,
      labelPadding: s.labelPadding ?? 2,
      rotate: s.rotate,
      showBarcode: s.showBarcode,
      showQr: s.showQR,
      pageMarginTop: s.pageMarginTop ?? 10,
      pageMarginRight: s.pageMarginRight ?? 10,
      pageMarginBottom: s.pageMarginBottom ?? 10,
      pageMarginLeft: s.pageMarginLeft ?? 10,
      containerPadding: s.containerPadding ?? 16,
      cellPadding: s.cellPadding ?? 8,
      tableColumns: s.columns ?? 4,
      tableCellWidth: s.cellWidth ?? 150,
      tableCellHeight: s.cellHeight ?? 40,
      tableCellGap: s.cellGap ?? 8,
      pageSize: s.pageSize ?? 'A4_PORTRAIT',
      currency: s.currency ?? 'Lei',
      labelSectionsJson: s.labelSections ? JSON.stringify(s.labelSections) : ''
    });
  }

  async function applySettings(s: { 
    width: number; height: number; count: number; numberOfCopies?: number; currency?: string; gap: number; 
    showBarcode: boolean; showQR: boolean; rotate: boolean; offset: number;
    pageMarginTop?: number; pageMarginRight?: number; pageMarginBottom?: number; pageMarginLeft?: number;
    containerPadding?: number; cellPadding?: number; labelPadding?: number;
    columns?: number; cellWidth?: number; cellHeight?: number; cellGap?: number;
    pageSize?: string;
    labelSections?: LabelSection[];
    printCellBorder?: boolean;
    printFitContent?: boolean;
  }) {
    setLabelW(s.width); setLabelH(s.height); setGap(s.gap); setCount(s.count);
    setShowBarcode(s.showBarcode); setShowQR(s.showQR); setRotate(s.rotate); setOffset(s.offset);
    if (s.currency !== undefined) setCurrency(s.currency);
    
    // Update padding settings
    if (s.pageMarginTop !== undefined) setPageMarginTop(s.pageMarginTop);
    if (s.pageMarginRight !== undefined) setPageMarginRight(s.pageMarginRight);
    if (s.pageMarginBottom !== undefined) setPageMarginBottom(s.pageMarginBottom);
    if (s.pageMarginLeft !== undefined) setPageMarginLeft(s.pageMarginLeft);
    if (s.containerPadding !== undefined) setContainerPadding(s.containerPadding);
    if (s.cellPadding !== undefined) setCellPadding(s.cellPadding);
    if (s.labelPadding !== undefined) setLabelPadding(s.labelPadding);
    
    // Update table layout settings
    if (s.columns !== undefined) setColumns(s.columns);
    if (s.cellWidth !== undefined) setCellWidth(s.cellWidth);
    if (s.cellHeight !== undefined) setCellHeight(s.cellHeight);
    if (s.cellGap !== undefined) setCellGap(s.cellGap);
    if (s.pageSize !== undefined) setPageSize(s.pageSize);
    if (s.printCellBorder !== undefined) setPrintCellBorder(s.printCellBorder);
    if (s.printFitContent !== undefined) setPrintFitContent(s.printFitContent);
    
    // Update label template configuration
    if (s.labelSections !== undefined) setLabelSections(s.labelSections);
    
    setPrintArgs(s);
    setPrintSettingsOpen(false);
    // Skip preview, go directly to print
  }

  const productsToPrint = useMemo(() => {
    if (printAllMode) return filteredProducts;
    return printSingleProduct ? [printSingleProduct] : [];
  }, [printAllMode, filteredProducts, printSingleProduct]);

  // Memoize callback functions to prevent unnecessary re-renders
  const handleFilterChange = useCallback((filterType: string, value: string) => {
    setPage(1); // Reset to first page when filtering
    
    switch (filterType) {
      case 'name':
        setFilterName(value);
        break;
      case 'firm':
        setFilterFirm(value);
        break;
      case 'model':
        setFilterModel(value);
        break;
      case 'size':
        setFilterSize(value);
        break;
      case 'category':
        setFilterCategory(value);
        break;
    }
  }, []);

  const clearFilters = useCallback(() => {
    setFilterName('');
    setFilterFirm('');
    setFilterModel('');
    setFilterSize('');
    setFilterCategory('');
    setPage(1);
  }, []);

  const closeProductFormModal = useCallback(() => {
    setShowForm(false);
    setEditingProduct(null);
  }, []);

  useEffect(() => {
    if (!showForm) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeProductFormModal();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [showForm, closeProductFormModal]);

  return (
    <div style={{ display: 'block', background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px #0001', padding: 32, margin: '32px auto', minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      {!subscriptionActive && (
        <div style={{
          background: '#fff3e0', border: '1px solid #ff9800', borderRadius: 8,
          padding: '12px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <span style={{ color: '#e65100', fontWeight: 600 }}>
            {t('productList.subscriptionInactive')}
          </span>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 24 }}>
        <div>
          <h2 style={{ color: '#1976d2', marginBottom: 8 }}>{t('productList.title')}</h2>
          {selectedCompanyId && (
            <div style={{ color: '#666', fontSize: 14 }}>
              {t('productList.company')}: <strong>{companies.find(c => c.id === selectedCompanyId)?.name}</strong>
              {companies.find(c => c.id === selectedCompanyId)?.location &&
                <span> ({companies.find(c => c.id === selectedCompanyId)?.location})</span>
              }
            </div>
          )}
          <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
            {t('productList.totalProducts', { count: products.length })} | {t('productList.filteredProducts', { count: filteredProducts.length })}
            {showHidden && <span style={{ color: '#e53935', fontWeight: 600 }}> | {t('productList.includingHidden')}</span>}
            {loading && <span style={{ color: '#1976d2' }}> | {t('common.loading')}</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 12, color: '#666' }}>
          {role === 'ROLE_ADMIN' && (
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#1976d2', textAlign: 'left' }}>
                {t('productList.userScope')}:
              </label>
              <select
                value={selectedUserId ?? ''}
                onChange={e => {
                  const value = e.target.value ? Number(e.target.value) : null;
                  setSelectedUserId(value);
                  localStorage.removeItem('selectedCompanyId');
                }}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #1976d2', minWidth: 200 }}
              >
                <option value="">{t('productList.myCompanies')}</option>
                {adminUsers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.username} ({u.role?.replace('ROLE_', '')})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>StockFlow</div>
          <div>{new Date().toLocaleDateString()}</div>
        </div>
      </div>

      {/* Company picker */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#1976d2' }}>
          {t('productList.company')}:
        </label>
        <select
          value={selectedCompanyId ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            const newCompanyId = v ? Number(v) : null;
            setSelectedCompanyId(newCompanyId);
            localStorage.setItem('selectedCompanyId', v);
          }}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #1976d2', fontSize: 14, minWidth: 220 }}
          disabled={companies.length === 0}
        >
          {!selectedCompanyId && <option value="">{t('productList.selectCompany')}</option>}
          {companies.map(c => (
            <option key={c.id} value={c.id}>
              {c.name}{c.location ? ` (${c.location})` : ''}
            </option>
          ))}
          {companies.length === 0 && <option value="">{t('productList.noCompaniesAvailable')}</option>}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => {
            if (filteredProducts.length > 0) {
              const lastProduct = filteredProducts[filteredProducts.length - 1];
              setEditingProduct(lastProduct);
              setShowForm(true);
            } else {
              setEditingProduct(null);
              setShowForm(true);
            }
          }}
          style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', fontWeight: 600, opacity: (!selectedCompanyId || !subscriptionActive) ? 0.5 : 1 }}
          disabled={selectedCompanyId === null || !subscriptionActive}
          title={!subscriptionActive ? t('productList.subscriptionRequired') : selectedCompanyId === null ? t('productList.pleaseSelectCompany') :
              filteredProducts.length > 0 ? t('productList.editLastProduct') : t('productList.addProduct')}>
          + {filteredProducts.length > 0 ? t('productList.editLastProduct') : t('productList.addProduct')}
        </button>
        <button onClick={exportToCSV} style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', fontWeight: 600 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: 'middle' }}>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7,10 12,15 17,10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {t('productList.exportCSV')}
        </button>
        <button onClick={() => openSettings({ all: true })} style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', fontWeight: 600 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: 'middle' }}>
            <polyline points="6,9 6,2 18,2 18,9" />
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          {t('productList.printAll')}
        </button>
        {(role === 'ROLE_ADMIN' || role === 'ROLE_WAREHOUSE') && (
          <button
            onClick={deleteAllProducts}
            style={{ background: '#f44336', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', fontWeight: 600, marginLeft: '8px' }}
            disabled={filteredProducts.length === 0}
            title={filteredProducts.length === 0 ? t('productList.noProductsToDelete') : t('productList.deleteAllCount', { count: filteredProducts.length })}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: 'middle' }}>
              <path d="M3 6h18" />
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              <line x1="10" y1="11" x2="10" y2="17" />
              <line x1="14" y1="11" x2="14" y2="17" />
            </svg>
            {t('productList.deleteAll')} ({filteredProducts.length})
          </button>
        )}
        {(role === 'ROLE_ADMIN' || role === 'ROLE_WAREHOUSE') && (
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 16,
            cursor: 'pointer', userSelect: 'none',
            padding: '6px 14px', borderRadius: 6,
            border: `1px solid ${showHidden ? '#e53935' : '#bbb'}`,
            background: showHidden ? '#fff5f5' : '#fafafa',
            color: showHidden ? '#e53935' : '#555',
            fontWeight: showHidden ? 700 : 400, fontSize: 13,
            transition: 'all 0.15s',
          }}>
            <input
              type="checkbox"
              checked={showHidden}
              onChange={e => setShowHidden(e.target.checked)}
              style={{ accentColor: '#e53935', width: 15, height: 15 }}
            />
            {t('productList.showHiddenProducts')}
          </label>
        )}
      </div>

      {showForm && (
        <div
          onClick={closeProductFormModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.55)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              width: 'min(980px, calc(100vw - 48px))',
              maxHeight: 'calc(100vh - 48px)',
              overflowY: 'auto',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 16px 48px rgba(0, 0, 0, 0.3)',
              padding: 20,
            }}
          >
            <button
              onClick={closeProductFormModal}
              title={t('common.close')}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                background: '#fff',
                border: '1px solid #ddd',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 22,
                color: '#444',
                width: 34,
                height: 34,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <ProductForm
              product={editingProduct || undefined}
              selectedCompanyId={selectedCompanyId ? Number(selectedCompanyId) : null}
              latestProduct={products.length > 0 && !editingProduct ? products[0] : undefined}
              lastCompanyProduct={lastCompanyProduct}
              onSaved={async (saved) => {
                const res = await CompanyApi.products(Number(selectedCompanyId), showHidden);
                setProducts(res.data);
                setEditingProduct(saved);
              }}
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="product-filters">
        <input placeholder={t('productList.filterName')} value={filterName} onChange={e => handleFilterChange('name', e.target.value)} />
        <input placeholder={t('productList.filterFirm')} value={filterFirm} onChange={e => handleFilterChange('firm', e.target.value)} />
        <input placeholder={t('productList.filterModel')} value={filterModel} onChange={e => handleFilterChange('model', e.target.value)} />
        <input placeholder={t('productList.filterSize')} value={filterSize} onChange={e => handleFilterChange('size', e.target.value)} />
        <select value={filterCategory} onChange={e => handleFilterChange('category', e.target.value)} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14 }}>
          <option value="">{t('productList.allCategories')}</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {(filterName || filterFirm || filterModel || filterSize || filterCategory) && (
          <button type="button" className="product-filter-clear" onClick={clearFilters}>{t('productList.clearFilters')}</button>
        )}
      </div>

      {/* Pagination header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label>{t('productList.rowsPerPage')}:</label>
          <select value={rowsPerPage} onChange={e => { setRowsPerPage(+e.target.value); setPage(1); }} style={{ padding: '4px 8px' }}>
            <option value={5}>5</option><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
          </select>
          <span style={{ color: '#1976d2', fontWeight: 600 }}>{t('productList.total')}: {products.length}</span>
          <span style={{ color: '#1976d2' }}>({t('productList.filtered')}: {filteredProducts.length})</span>
        </div>
        <div>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ marginRight: 8, padding: '4px 8px' }}>{t('productList.previous')}</button>
          <span>{t('productList.pageOf', { page, total: paginationData.totalPages })}</span>
          <button onClick={() => setPage(p => Math.min(paginationData.totalPages, p + 1))} disabled={page === paginationData.totalPages} style={{ marginLeft: 8, padding: '4px 8px' }}>{t('productList.next')}</button>
        </div>
      </div>

      {/* Table */}
      <p className="product-table-scroll-hint" aria-hidden="true">↔ {t('productList.scrollHint')}</p>
      <div className="print-container product-table-scroll" tabIndex={0} role="region" aria-label={t('productList.scrollHint')}>
        <table className="product-data-table" style={{ width: 'max-content', borderCollapse: 'separate', borderSpacing: 0, background: '#f5f8fa', borderRadius: 8, boxShadow: '0 1px 4px #0001', minWidth: '1200px', tableLayout: 'auto' }}>
          <thead>
            <tr style={{ background: '#1976d2', color: '#fff' }}>
              <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '100px', maxWidth: '120px', whiteSpace: 'nowrap' }}>{t('product.image')}</th>
              <th style={{ padding: '12px 10px', textAlign: 'left', minWidth: '120px', maxWidth: '150px' }}>{t('common.name')}</th>
              <th style={{ padding: '12px 10px', textAlign: 'left', minWidth: '80px', maxWidth: '120px' }}>{t('product.firm')}</th>
              <th style={{ padding: '12px 10px', textAlign: 'left', minWidth: '80px', maxWidth: '120px' }}>{t('product.model')}</th>
              <th style={{ padding: '12px 10px', textAlign: 'left', minWidth: '100px', maxWidth: '150px' }}>{t('product.description')}</th>
              <th style={{ padding: '12px 10px', textAlign: 'left', minWidth: '80px', maxWidth: '120px' }}>{t('product.headerText')}</th>
              <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '60px', maxWidth: '80px' }}>{t('common.size')}</th>
              <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '70px', maxWidth: '90px' }}>{t('common.price')}</th>
              <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '80px', maxWidth: '120px' }}>{t('product.category')}</th>
              <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '80px', maxWidth: '120px' }}>{t('common.company')}</th>
              <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '280px' }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {paginationData.paginatedProducts.map((p, idx) => (
              <tr key={p.id} style={{
                background: p.isDeleted ? '#fff0f0' : (idx % 2 === 0 ? '#fff' : '#f0f4fa'),
                borderBottom: '2px solid #e0e0e0',
                opacity: p.isDeleted ? 0.75 : 1,
              }}>
                <td style={{ padding: '12px 10px', textAlign: 'center', minWidth: '100px', maxWidth: '120px' }}>
                  {getImageUrl(p.imageUrl) ? (
                    <img 
                      src={getImageUrl(p.imageUrl)} 
                      alt={p.name} 
                      style={{ 
                        width: '50px', 
                        height: '50px', 
                        objectFit: 'cover', 
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                      }} 
                    />
                  ) : (
                    <div style={{ 
                      width: '50px', 
                      height: '50px', 
                      backgroundColor: '#f0f0f0', 
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      color: '#999'
                    }}>
                      {t('productList.noImage')}
                    </div>
                  )}
                </td>
                <td style={{ padding: '12px 10px', minWidth: '120px', maxWidth: '150px', wordWrap: 'break-word' }}><strong>{p.name}</strong></td>
                <td style={{ padding: '12px 10px', minWidth: '80px', maxWidth: '120px', wordWrap: 'break-word' }}>{p.firm}</td>
                <td style={{ padding: '12px 10px', minWidth: '80px', maxWidth: '120px', wordWrap: 'break-word' }}>{p.model}</td>
                <td style={{ padding: '12px 10px', minWidth: '100px', maxWidth: '150px', wordWrap: 'break-word' }}>{p.description}</td>
                <td style={{ padding: '12px 10px', fontStyle: 'italic', color: '#666', minWidth: '80px', maxWidth: '120px', wordWrap: 'break-word' }}>{p.headerText || t('productList.noHeader')}</td>
                <td style={{ padding: '12px 10px', textAlign: 'center', minWidth: '60px', maxWidth: '80px' }}>{p.size}</td>
                <td style={{ padding: '12px 10px', textAlign: 'center', minWidth: '70px', maxWidth: '90px' }}>
                  {editingPriceId === p.id ? (
                    <input
                      type="number"
                      step="0.01"
                      value={editingPriceValue}
                      onChange={handlePriceChange}
                      onBlur={handlePriceBlur}
                      onKeyDown={handlePriceKeyDown}
                      autoFocus
                      className="no-spinner"
                      style={{
                        width: '100%',
                        padding: '4px',
                        border: '2px solid #1976d2',
                        borderRadius: '4px',
                        textAlign: 'center',
                        fontSize: '14px'
                      }}
                    />
                  ) : (
                    <span
                      onDoubleClick={() => handlePriceDoubleClick(p)}
                      style={{
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        display: 'inline-block',
                        transition: 'background 0.2s'
                      }}
                      title={t('productList.doubleClickToEdit')}
                    >
                      {p.price}
                    </span>
                  )}
                </td>
                <td style={{ padding: '12px 10px', textAlign: 'center', minWidth: '80px', maxWidth: '120px', wordWrap: 'break-word' }}>{p.category?.name}</td>
                <td style={{ padding: '12px 10px', textAlign: 'center', minWidth: '80px', maxWidth: '120px' }}>
                  {p.companies && p.companies.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {p.companies.map((company) => (
                        <span key={company.id} style={{
                          background: '#e3f2fd',
                          color: '#1976d2',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {company.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>{t('productList.noCompany')}</span>
                  )}
                </td>
                <td style={{ padding: '12px 10px', textAlign: 'center', whiteSpace: 'nowrap', minWidth: '280px' }}>
                  {(role === 'ROLE_ADMIN' || role === 'ROLE_WAREHOUSE') && (
                    <button
                      onClick={() => { if (subscriptionActive) { setEditingProduct(p); setShowForm(true); } }}
                      title={subscriptionActive ? t('productList.edit') : t('productList.subscriptionRequired')}
                      disabled={!subscriptionActive}
                      style={{ marginRight: 8, background: 'none', border: 'none', cursor: subscriptionActive ? 'pointer' : 'not-allowed', fontSize: 16, color: subscriptionActive ? '#1976d2' : '#bbb' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z" />
                      </svg>
                    </button>
                  )}
                  {role === 'ROLE_ADMIN' && (
                    <button onClick={() => handleOpenCompanyModal(p)} title={t('productList.manageCompanies')} style={{ marginRight: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#1976d2' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 21h18" />
                        <path d="M5 21V7l8-4v18" />
                        <path d="M19 21V11l-6-4" />
                        <path d="M9 9v.01" />
                        <path d="M9 12v.01" />
                        <path d="M9 15v.01" />
                        <path d="M9 18v.01" />
                      </svg>
                    </button>
                  )}
                  {(role === 'ROLE_ADMIN' || role === 'ROLE_WAREHOUSE') && (
                    <button onClick={() => p.id && handleHideProduct(p.id)} title={t('productList.hideProduct')} aria-label={t('productList.hideProduct')} style={{ marginRight: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#607d8b' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                        <circle cx="12" cy="12" r="3" />
                        <line x1="3" y1="3" x2="21" y2="21" />
                      </svg>
                    </button>
                  )}
                  {(role === 'ROLE_ADMIN' || role === 'ROLE_WAREHOUSE') && showHidden && (
                    <button
                      onClick={() => p.id && handleHardDeleteProduct(p.id, p.name)}
                      title={t('productList.permanentDelete')}
                      style={{ marginRight: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#b71c1c' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                        <line x1="4" y1="4" x2="20" y2="20" />
                      </svg>
                    </button>
                  )}
                  {(role === 'ROLE_ADMIN' || role === 'ROLE_WAREHOUSE') && showHidden && (
                    <label
                      title={t('productList.toggleHidden')}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        marginRight: 8, fontSize: 11, color: '#444', cursor: 'pointer',
                        padding: '2px 6px', borderRadius: 4, border: '1px solid #ddd',
                        background: '#fafafa'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!p.isDeleted}
                        onChange={e => handleToggleHidden(p, e.target.checked)}
                        style={{ accentColor: '#e53935', width: 14, height: 14 }}
                      />
                      {t('productList.hidden')}
                    </label>
                  )}
                  <button onClick={() => openSettings({ all: false, product: p })} title={t('productList.printLabels')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#1976d2' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6,9 6,2 18,2 18,9" />
                      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                      <rect x="6" y="14" width="12" height="8" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* SETTINGS */}
      {printSettingsOpen && (
        <PrintSettingsPopup
          onClose={() => { setPrintSettingsOpen(false); setPrintAllMode(false); setPrintSingleProduct(null); setExpandedPrintProducts(null); }}
          onConfirm={applySettings}
          onSave={saveSettingsOnly}
          isPrintAll={printAllMode}
          products={productsToPrint}
          onProductsConfirm={setExpandedPrintProducts}
          defaults={{
            width: labelW, height: labelH, gap, count, showBarcode, showQR, rotate, offset,
            currency,
            pageMarginTop, pageMarginRight, pageMarginBottom, pageMarginLeft, 
            containerPadding, cellPadding, labelPadding,
            columns, cellWidth, cellHeight, cellGap, pageSize,
            labelSections,
            printCellBorder, printFitContent
          }}
        />
      )}

      {/* HIDDEN PRINT TRIGGER */}
      {printArgs && (printAllMode ? productsToPrint.length > 0 : !!printSingleProduct) && (
        <PrintLabel
          products={expandedPrintProducts ?? productsToPrint}
          count={printArgs.count}
          numberOfCopies={printArgs.numberOfCopies ?? 1}
          currency={printArgs.currency ?? 'Lei'}
          labelW={printArgs.width}
          labelH={printArgs.height}
          gap={printArgs.gap}
          rotate={printArgs.rotate}
          showBarcode={printArgs.showBarcode}
          showQR={printArgs.showQR}
          offset={printArgs.offset}
          labelPadding={printArgs.labelPadding}
          pageMargins={{
            top: printArgs.pageMarginTop ?? 10,
            right: printArgs.pageMarginRight ?? 10,
            bottom: printArgs.pageMarginBottom ?? 10,
            left: printArgs.pageMarginLeft ?? 10
          }}
          labelSections={labelSections}
          companies={companies}
          selectedCompanyId={selectedCompanyId}
          printCellBorder={printArgs.printCellBorder}
          printFitContent={printArgs.printFitContent}
          onDone={() => {
            setPrintArgs(null);
            setPrintAllMode(false);
            setPrintSingleProduct(null);
            setExpandedPrintProducts(null);
          }}
        />
      )}

      {/* Company Management Modal */}
      {companyModalOpen && companyModalProduct && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="company-management-title"
          style={{ position: 'fixed', inset: 0, background: '#0008', zIndex: 1000, display: 'grid', placeItems: 'center', padding: 20 }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 'min(460px, 100%)', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 6px 30px #0004' }}>
            <h3 id="company-management-title" style={{ margin: '0 0 8px', color: '#1976d2' }}>
              {t('productList.manageCompanies')}
            </h3>
            <p style={{ margin: '0 0 18px', color: '#555' }}>{companyModalProduct.name}</p>
            <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
              {companies.map(company => (
                <label key={company.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', border: '1px solid #ddd', borderRadius: 7, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedCompaniesForProduct.includes(company.id)}
                    onChange={() => toggleCompanyForProduct(company.id)}
                  />
                  <span>{company.name}</span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button type="button" onClick={closeCompanyManagementModal} style={{ padding: '8px 16px', background: '#eee', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                {t('common.cancel')}
              </button>
              <button type="button" onClick={updateProductCompanies} style={{ padding: '8px 16px', background: '#1976d2', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print-specific styles */}
      <style>{`
        /* Hide number input spin buttons for price field */
        input.no-spinner::-webkit-inner-spin-button,
        input.no-spinner::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input.no-spinner {
          -moz-appearance: textfield;
        }
        @media print {
          /* ── hide UI chrome ── */
          button, .pagination, .filters, .search-box {
            display: none !important;
          }

          /* ── page setup ── */
          @page {
            size: A4 landscape !important;
            margin: ${pageMarginTop}mm ${pageMarginRight}mm ${pageMarginBottom}mm ${pageMarginLeft}mm !important;
          }

          /* ── container ── */
          .print-container {
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }

          /* ── table skeleton ──
             table-layout:fixed means the browser respects th widths
             and never lets any cell exceed its column.              */
          table {
            width: 100% !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
            border: ${printCellBorder ? '1px solid #aaa' : 'none'} !important;
            font-size: 7px !important;
            margin: 0 !important;
            page-break-inside: auto !important;
          }
          thead { display: table-header-group !important; }
          tr    { page-break-inside: avoid !important; }

          /* Neutralise inline min-width / max-width set by React styles */
          th, td { min-width: 0 !important; max-width: none !important; }

          /* ── column widths (sum = 100%) ── */
          col:nth-child(1),  th:nth-child(1),  td:nth-child(1)  { width: 8% !important;  }  /* image */
          col:nth-child(2),  th:nth-child(2),  td:nth-child(2)  { width: 11% !important; }  /* name */
          col:nth-child(3),  th:nth-child(3),  td:nth-child(3)  { width: 8% !important;  }  /* firm */
          col:nth-child(4),  th:nth-child(4),  td:nth-child(4)  { width: 8% !important;  }  /* model */
          col:nth-child(5),  th:nth-child(5),  td:nth-child(5)  { width: 13% !important; }  /* description */
          col:nth-child(6),  th:nth-child(6),  td:nth-child(6)  { width: 10% !important; }  /* header text */
          col:nth-child(7),  th:nth-child(7),  td:nth-child(7)  { width: 6% !important;  }  /* size */
          col:nth-child(8),  th:nth-child(8),  td:nth-child(8)  { width: 6% !important;  }  /* price */
          col:nth-child(9),  th:nth-child(9),  td:nth-child(9)  { width: 9% !important;  }  /* category */
          col:nth-child(10), th:nth-child(10), td:nth-child(10) { width: 10% !important; }  /* company */
          col:nth-child(11), th:nth-child(11), td:nth-child(11) { width: 11% !important; }  /* actions */

          /* ── header cells ── */
          th {
            background: #1976d2 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color: white !important;
            padding: ${cellPadding}px 3px !important;
            font-size: 7px !important;
            font-weight: bold !important;
            text-align: center !important;
            border: ${printCellBorder ? '1px solid #888' : 'none'} !important;
            white-space: normal !important;
            word-break: break-word !important;
            overflow: hidden !important;
            vertical-align: middle !important;
            line-height: 1.2 !important;
            box-sizing: border-box !important;
          }

          /* ── data cells ──
             table-layout:fixed clamps each cell to its column width.
             When printFitContent=true  → JS scales .print-cell-inner to fit; overflow hidden.
             When printFitContent=false → overflow:visible, rows can grow.    */
          td {
            padding: ${cellPadding}px 3px !important;
            font-size: 7px !important;
            border: ${printCellBorder ? '1px solid #ccc' : 'none'} !important;
            white-space: normal !important;
            word-break: break-word !important;
            overflow: hidden !important;
            vertical-align: top !important;
            line-height: 1.2 !important;
            box-sizing: border-box !important;
            max-width: 0 !important;
            ${printFitContent ? `height: ${cellHeight}px !important; max-height: ${cellHeight}px !important;` : ''}
          }

          /* inner wrapper injected by beforeprint JS handler */
          .print-cell-inner {
            display: inline-block !important;
            transform-origin: top left !important;
            max-width: none !important;
          }

          /* ── alternating rows ── */
          tr:nth-child(even) td { background: #f7f7f7 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

          /* ── images ── */
          td img {
            display: block !important;
            max-width: 100% !important;
            max-height: ${printFitContent ? Math.max(cellHeight - cellPadding * 2, 12) : 60}px !important;
            width: auto !important;
            height: auto !important;
            object-fit: contain !important;
          }

          /* ── no-image placeholder div ── */
          td > div[style] {
            width: auto !important;
            max-width: 100% !important;
            height: auto !important;
            font-size: 6px !important;
          }

          /* ── barcode / QR wrappers ── */
          .barcode-container, .qr-container {
            max-width: 100% !important;
            overflow: hidden !important;
          }
          .barcode-container svg,
          .barcode-container img,
          .barcode-container canvas,
          .qr-container svg {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
          }

          /* ── company / tag badges ── */
          td span[style] {
            display: block !important;
            white-space: normal !important;
            word-break: break-word !important;
            overflow: hidden !important;
            max-width: 100% !important;
            font-size: 6px !important;
          }
        }
      `}</style>
    </div>
  );
}

